import { useState, useEffect, useRef } from "react";
import { Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox, ToggleButtonGroup, ToggleButton, Tooltip, Alert } from "@mui/material";
import InteractiveDiagramCanvas from "../../Student/components/InteractiveDiagramCanvas";

const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

const r4 = (v) => Math.round(v * 1e4) / 1e4;

// Rectangular grid of cells over a bounding box → region polygons (fractional image coords).
const genRectRegions = (b) => {
  const x = num(b.x, 0.1), y = num(b.y, 0.08), w = num(b.w, 0.8), h = num(b.h, 0.84);
  const rows = Math.max(1, Math.round(num(b.rows, 4))), cols = Math.max(1, Math.round(num(b.cols, 4)));
  const out = [];
  const cw = w / cols, ch = h / rows;
  let id = 0;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x0 = x + c * cw, y0 = y + r * ch;
      out.push({ id: id++, cell: [c, r], points: [[x0, y0], [x0 + cw, y0], [x0 + cw, y0 + ch], [x0, y0 + ch]] });
    }
  }
  return out;
};

// Rows×cols lattice of tappable circles (cx/cy/r). Used for "complete the pattern" diagrams where
// positions are pre-drawn and the student only chooses which to fill. x/y = first centre; dx/dy =
// centre spacing (falls back to w/(cols-1) and h/(rows-1) when dx/dy are blank).
const genCircleLatticeRegions = (b) => {
  const x0 = num(b.x, 0.1), y0 = num(b.y, 0.1);
  const rows = Math.max(1, Math.round(num(b.rows, 4))), cols = Math.max(1, Math.round(num(b.cols, 4)));
  const dx = num(b.dx, 0) > 0 ? num(b.dx) : (cols > 1 ? num(b.w, 0.8) / (cols - 1) : Math.max(num(b.w, 0.1), 0.05));
  const dy = num(b.dy, 0) > 0 ? num(b.dy) : (rows > 1 ? num(b.h, 0.84) / (rows - 1) : Math.max(num(b.h, 0.1), 0.05));
  const r = Math.max(0.005, num(b.r, Math.min(dx, dy) * 0.35));
  const out = [];
  let id = 0;
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      out.push({ id: id++, cell: [i, j], cx: r4(x0 + i * dx), cy: r4(y0 + j * dy), r: r4(r) });
    }
  }
  return out;
};

const inferCircleLattice = (regions) => {
  const circ = (regions || []).filter((rg) => rg && rg.r != null && Number.isFinite(num(rg.cx)) && Number.isFinite(num(rg.cy)));
  if (circ.length < 1) return null;
  const xs = [...new Set(circ.map((rg) => r4(num(rg.cx))))].sort((a, b) => a - b);
  const ys = [...new Set(circ.map((rg) => r4(num(rg.cy))))].sort((a, b) => a - b);
  // Average span (not first gap) — consecutive r4 centres can lose a digit of dx/dy.
  // Keep 5dp on spacing so regen lands on the same r4 centres as the original lattice.
  const dx = xs.length > 1 ? (xs[xs.length - 1] - xs[0]) / (xs.length - 1) : 0.1;
  const dy = ys.length > 1 ? (ys[ys.length - 1] - ys[0]) / (ys.length - 1) : 0.1;
  const r5 = (v) => Math.round(v * 1e5) / 1e5;
  return {
    x: String(xs[0] ?? 0.1),
    y: String(ys[0] ?? 0.1),
    dx: String(r5(dx)),
    dy: String(r5(dy)),
    w: String(r4(xs.length > 1 ? xs[xs.length - 1] - xs[0] : dx)),
    h: String(r4(ys.length > 1 ? ys[ys.length - 1] - ys[0] : dy)),
    cols: String(xs.length || 1),
    rows: String(ys.length || 1),
    r: String(r4(num(circ[0].r, 0.035))),
  };
};

const latticePatchFromBbox = (b) => {
  const rows = Math.max(1, Math.round(num(b.rows, 4)));
  const cols = Math.max(1, Math.round(num(b.cols, 4)));
  const dx = num(b.dx, 0) > 0 ? num(b.dx) : (cols > 1 ? num(b.w, 0.8) / (cols - 1) : Math.max(num(b.w, 0.1), 0.05));
  const dy = num(b.dy, 0) > 0 ? num(b.dy) : (rows > 1 ? num(b.h, 0.84) / (rows - 1) : Math.max(num(b.h, 0.1), 0.05));
  const r = Math.max(0.005, num(b.r, Math.min(dx, dy) * 0.35));
  const r5 = (v) => Math.round(v * 1e5) / 1e5;
  return { x: num(b.x, 0.1), y: num(b.y, 0.1), dx: r5(dx), dy: r5(dy), rows, cols, r: r4(r) };
};

// Equilateral-triangle figure subdivided into `rows` rows of small triangles (the common 11+
// fraction-of-a-triangle diagram). bbox is the triangle's bounding box (apex top-centre).
const genTriangleRegions = (b) => {
  const x = num(b.x, 0.1), y = num(b.y, 0.08), w = num(b.w, 0.8), h = num(b.h, 0.84);
  const rows = Math.max(1, Math.round(num(b.rows, 4)));
  // Omit the top `topSkip` (pointed apex) rows so the generated grid matches a figure whose apex is
  // cropped — the remaining rows form a truncated triangle (trapezoid) with a flat top edge.
  const topSkip = Math.max(0, Math.min(rows - 1, Math.round(num(b.topSkip, 0))));
  const out = [];
  let id = 0;
  const apexX = x + w / 2;
  const rowH = h / rows;
  for (let r = topSkip; r < rows; r++) {
    // row r (0 at top) spans vertical band [y+r*rowH, y+(r+1)*rowH]; has r+1 "up" and r "down" triangles
    const topY = y + r * rowH, botY = y + (r + 1) * rowH;
    const topHalfW = (w / 2) * (r / rows);
    const botHalfW = (w / 2) * ((r + 1) / rows);
    const topL = apexX - topHalfW, topR = apexX + topHalfW;
    const botL = apexX - botHalfW, botR = apexX + botHalfW;
    const upCount = r + 1;
    const cellW = (botR - botL) / upCount;
    for (let k = 0; k < upCount; k++) {
      const bx = botL + k * cellW;
      out.push({ id: id++, points: [[bx, botY], [bx + cellW, botY], [bx + cellW / 2, topY]] });
    }
    const topW = topR - topL;
    const downCount = r;
    const dCellW = downCount > 0 ? topW / downCount : 0;
    for (let k = 0; k < downCount; k++) {
      const tx = topL + k * dCellW;
      const apexBx = botL + cellW * (k + 1);
      out.push({ id: id++, points: [[tx, topY], [tx + dCellW, topY], [apexBx, botY]] });
    }
  }
  return out;
};

// Regular polygon (bounding box x,y,w,h) split into `sides` equal triangular sectors from the
// centre — the "draw lines from the centre, shade N% of this shape" diagram. rot rotates the
// vertices; rot=0 puts a flat side at the top (the common decagon/octagon orientation).
const genSectorRegions = (b) => {
  const x = num(b.x, 0.1), y = num(b.y, 0.08), w = num(b.w, 0.8), h = num(b.h, 0.84);
  const n = Math.max(3, Math.round(num(b.sides, 10)));
  const rot = num(b.rot, 0);
  const cx = x + w / 2, cy = y + h / 2, rx = w / 2, ry = h / 2;
  const ang = (k) => ((-90 + rot + (360 / n) * (k + 0.5)) * Math.PI) / 180;
  const v = [];
  for (let k = 0; k < n; k++) v.push([cx + rx * Math.cos(ang(k)), cy + ry * Math.sin(ang(k))]);
  const out = [];
  for (let k = 0; k < n; k++) out.push({ id: k, points: [[cx, cy], v[k], v[(k + 1) % n]] });
  return out;
};

// N-pointed star (bounding box x,y,w,h) split into its 2N small triangles — the "shade a⁄b of this
// star" diagram. `sides` = number of points; `inner` = valley radius as a fraction of the tip radius;
// rot=0 puts one tip straight up. Inner triangle k = [centre, valleyₖ, valleyₖ₊₁]; outer point
// triangle k = [valleyₖ, tipₖ₊₁, valleyₖ₊₁] (tip sits between two valleys).
const genStarRegions = (b) => {
  const x = num(b.x, 0.05), y = num(b.y, 0.03), w = num(b.w, 0.9), h = num(b.h, 0.94);
  const n = Math.max(3, Math.round(num(b.sides, 7)));
  const rot = num(b.rot, 0);
  const inner = Math.min(0.95, Math.max(0.1, num(b.inner, 0.6)));
  const cx = x + w / 2, cy = y + h / 2, rx = w / 2, ry = h / 2;
  const tips = [], val = [];
  for (let k = 0; k < n; k++) {
    const at = ((-90 + rot + (360 / n) * k) * Math.PI) / 180;
    tips.push([cx + rx * Math.cos(at), cy + ry * Math.sin(at)]);
    const av = ((-90 + rot + (360 / n) * (k + 0.5)) * Math.PI) / 180;
    val.push([cx + rx * inner * Math.cos(av), cy + ry * inner * Math.sin(av)]);
  }
  const out = [];
  let id = 0;
  for (let k = 0; k < n; k++) {
    out.push({ id: id++, points: [[cx, cy], val[k], val[(k + 1) % n]] });
    out.push({ id: id++, points: [val[k], tips[(k + 1) % n], val[(k + 1) % n]] });
  }
  return out;
};

// Proportional (unequal) sectors from a data table — e.g. "complete the pie chart" where each
// category's angle is its value's share of the total (value/total*360). `rows` = [{label, value,
// given}]. `given` rows (e.g. a category already shaded+labelled in the background image as a
// worked example) still consume their angular share so the remaining sectors land in the right
// place, but are excluded from the returned (interactive) regions — the student never taps them.
// `b.rot` is the start angle in degrees using screen coords (0 = 3 o'clock, clockwise positive,
// e.g. -90 = 12 o'clock). `b.sides` shapes the outer boundary: a regular n-gon with a vertex at the
// start angle (so each sector polygon follows the straight edges, hugging e.g. a 12-gon "clock"
// chart) — set it blank/large for a smooth circle. Each region carries a `label` (the answer text
// the student must type inside that sector — stripped from the student spec server-side, see
// InteractiveDiagramGrading::interactiveStudentSpec).
// `labelOnly`: the background picture already shows every sector shaded/divided correctly, so ALL
// rows (not just ones individually flagged `given`) are kept as `given: true` regions carrying their
// `label` — there is nothing to tap, only labels to type. Without this flag, `given` rows are dropped
// entirely (the existing "worked example already in the picture" behaviour, unchanged).
// `groupMode`: "colour by category" — the chart is cut into one region per UNIT (e.g. 24 one-hour
// slices for a 24-hour day) rather than one per category, and the student colours the right NUMBER of
// units per category (that division is the work). Rows flagged `given` still consume their units, but
// those units are locked (the worked example already printed on the chart). The category list itself
// travels separately as spec.groups — see genSectorGroups().
// `b.unit` is how much of the data ONE section stands for, so the table can hold the paper's real
// figures rather than slice counts: Whitgift's 100 boys (15/25/50/10) at unit 5 give 20 sections of
// 5 boys (3/5/10/2). Default 1 = one section per unit of value, which is what every chart authored
// before this field existed did.
const genProportionalSectorRegions = (rows, b, labelOnly = false, groupMode = false) => {
  const x = num(b.x, 0.1), y = num(b.y, 0.08), w = num(b.w, 0.8), h = num(b.h, 0.84);
  const startDeg = num(b.rot, -90);
  const sides = Math.round(num(b.sides, 0));
  const unit = Math.max(num(b.unit, 1), 1e-9);
  // Where the n-gon's own vertices sit. It defaults to the start angle (a vertex on the first sector's
  // edge — how every chart authored in this editor was drawn), but a printed chart's polygon need not
  // line up with its first sector at all: North London 2015's 12-gon has vertices at 15° while its
  // first slice starts at 255.5°. Without this the editor simply could not describe that chart.
  const vtxDeg = num(b.polyRot, startDeg);
  const cx = x + w / 2, cy = y + h / 2, rx = w / 2, ry = h / 2;
  const total = rows.reduce((s, r) => s + Math.max(0, num(r.value, 0)), 0) || 1;
  const D2R = Math.PI / 180;
  const r4 = (v) => Math.round(v * 1e4) / 1e4;
  // Radius factor at an angle: 1 on a circle; on a regular n-gon it dips to the apothem at each edge
  // midpoint so the boundary follows the straight edges instead of a chord.
  const rf = (deg) => {
    if (sides < 3) return 1;
    const seg = 360 / sides, half = seg / 2;
    let rel = (deg - vtxDeg) % seg;
    if (rel < 0) rel += seg;
    return Math.cos(half * D2R) / Math.cos((rel - half) * D2R);
  };
  const pt = (deg) => [r4(cx + rx * rf(deg) * Math.cos(deg * D2R)), r4(cy + ry * rf(deg) * Math.sin(deg * D2R))];
  // One wedge polygon from `from`° to `to`°: centre, the start edge, every n-gon vertex (or, for a
  // circle, an arc sample every 6°) strictly inside the span so the fill follows the boundary, then
  // the end edge.
  const wedge = (from, to) => {
    const pts = [[r4(cx), r4(cy)], pt(from)];
    if (sides >= 3) {
      const seg = 360 / sides;
      const k = Math.ceil((from - vtxDeg + 1e-6) / seg);
      for (let v = vtxDeg + k * seg; v < to - 1e-6; v += seg) pts.push(pt(v));
    } else {
      for (let t = from + 6; t < to - 1e-6; t += 6) pts.push(pt(t));
    }
    pts.push(pt(to));
    return pts;
  };
  const out = [];
  let deg = startDeg, id = 0;
  rows.forEach((row) => {
    const units = Math.max(0, num(row.value, 0));
    const span = (units / total) * 360;
    const end = deg + span;
    if (groupMode) {
      // One region per UNIT — the student colours the right NUMBER of them per category, so the
      // regions must be uniform and carry no label (the label lives on the category, not the slice).
      const n = Math.round(units / unit);
      for (let u = 0; u < n; u++) {
        const a = deg + (span * u) / n;
        const b2 = deg + (span * (u + 1)) / n;
        const r = { id: id++, points: wedge(a, b2) };
        if (row.given) r.given = true;
        out.push(r);
      }
    } else if (labelOnly) {
      out.push({ id: id++, points: wedge(deg, end), label: String(row.label || "").trim(), given: true });
    } else if (!row.given) {
      out.push({ id: id++, points: wedge(deg, end), label: String(row.label || "").trim() });
    }
    deg = end;
  });
  return out;
};

// The categories the student colours, for "colour by category" mode. A row flagged `given` is the
// worked example already printed on the chart (its units are locked), so it isn't a category the
// student has to produce. `count` is the number of unit-regions the category takes — the answer, which
// InteractiveDiagramGrading::interactiveStudentSpec strips before the spec reaches the student.
const genSectorGroups = (rows, unit = 1) => {
  const u = Math.max(num(unit, 1), 1e-9);
  return rows
    .filter((r) => !r.given && String(r.label || "").trim() !== "" && Math.round(num(r.value, 0) / u) > 0)
    .map((r) => ({
      key: String(r.label).trim().toLowerCase().replace(/\s+/g, "_"),
      label: String(r.label).trim(),
      count: Math.round(num(r.value, 0) / u),
    }));
};

// Re-opening a saved pie chart: recover the generator's bounding box / boundary / start angle from the
// stored regions, so re-running the generator reproduces THE SAME chart instead of the defaults (a
// 10-gon at 3 o'clock). Charts authored from now on carry `spec.chart` and skip this; older ones
// (North London 2015's 12-gon, Whitgift's circle) are measured back out of their own geometry.
// Every wedge starts at the centre, so points[0] is the centre and points[1] is its start edge.
const inferSectorBbox = (regions) => {
  const first = (regions || []).find((r) => Array.isArray(r.points) && r.points.length >= 3);
  if (!first) return null;
  const [cx, cy] = first.points[0].map(Number);
  const rim = regions.flatMap((r) => (r.points || []).slice(1).map((p) => [Number(p[0]), Number(p[1])]));
  // Aspect only — the widest rim point of an n-gon sits at its apothem, not its circumradius, so these
  // under-read the true radii by up to cos(180/n). The scale is fitted per candidate below.
  const ax = Math.max(...rim.map((p) => Math.abs(p[0] - cx)));
  const ay = Math.max(...rim.map((p) => Math.abs(p[1] - cy)));
  if (!(ax > 0) || !(ay > 0)) return null;
  const angleOf = (p) => (Math.atan2((p[1] - cy) / ay, (p[0] - cx) / ax) * 180) / Math.PI;
  // To 0.1° — a printed chart's first slice need not start on a whole degree (North London's is 255.5°),
  // and rounding it moved every wedge by a pixel or two.
  const rot = Math.round(angleOf(first.points[1]) * 10) / 10;
  // Boundary: on a circle every rim point sits at the same radius; on a regular n-gon the radius dips to
  // the apothem between vertices, by a factor that depends only on the angle. So for a candidate
  // (n, vertex phase) the ratio radius ÷ model should be CONSTANT (= the true radius) — score each
  // candidate by how much that ratio varies, which fits the unknown scale instead of assuming it.
  // The phase is searched, not assumed to be the start angle: a printed chart's corners need not line up
  // with its first sector (North London 2015 — 12-gon at 15°, first slice at 255.5°).
  const D2R = Math.PI / 180;
  const rfOf = (deg, n, phase) => {
    if (n < 3) return 1;
    const seg = 360 / n, half = seg / 2;
    let rel = (deg - phase) % seg;
    if (rel < 0) rel += seg;
    return Math.cos(half * D2R) / Math.cos((rel - half) * D2R);
  };
  const fit = (n, phase) => {
    const ks = rim.map((p) => Math.hypot((p[0] - cx) / ax, (p[1] - cy) / ay) / rfOf(angleOf(p), n, phase));
    const mean = ks.reduce((s, k) => s + k, 0) / ks.length;
    const cv = mean > 0 ? Math.sqrt(ks.reduce((s, k) => s + (k - mean) ** 2, 0) / ks.length) / mean : Infinity;
    return { cv, scale: mean };
  };
  let sides = 0, phase = rot, best = fit(0, 0);
  for (let n = 3; n <= 24; n++) {
    const seg = 360 / n;
    for (let ph = 0; ph < seg; ph += 0.5) {
      const f = fit(n, ph);
      if (f.cv < best.cv - 1e-4) { best = f; sides = n; phase = ph; }
    }
  }
  const rx = ax * best.scale, ry = ay * best.scale;
  const r4 = (v) => Math.round(v * 1e4) / 1e4;
  return {
    x: String(r4(cx - rx)), y: String(r4(cy - ry)), w: String(r4(2 * rx)), h: String(r4(2 * ry)),
    sides: String(sides), rot: String(rot), polyRot: sides >= 3 ? String(r4(phase)) : "",
  };
};

/**
 * Authoring widget for interactive "drawing" answer modes. Manages the full spec
 * (solution_geometry.interactive) including the secret targets; the admin taps the correct answer
 * directly on the diagram (WYSIWYG), guaranteeing it matches the grid the student sees.
 */
const InteractiveDiagramEditor = ({ kind, spec, onChange, imageUrl, contextLayers = null }) => {
  const safeSpec = spec && spec.kind === kind ? spec : { kind };
  // The labels shown in the "Point labels" field. They live in TWO places: the explicit point_labels list,
  // and inline on each target point ([3, 5, "A"]). Imported questions often only have the inline form, so
  // reading point_labels alone showed an EMPTY field for a question whose points are clearly labelled
  // A/B/C — and, because the "require the label" checkbox was gated on that same empty list, the label
  // could not be marked as graded at all. Fall back to the inline labels.
  const inlinePointLabels = Array.isArray(safeSpec.target_points)
    ? safeSpec.target_points.map((p) => (Array.isArray(p) && typeof p[2] === "string" ? p[2].trim() : ""))
    : [];
  const pointLabelsForField = (safeSpec.point_labels || []).some((l) => String(l || "").trim() !== "")
    ? safeSpec.point_labels
    : (inlinePointLabels.some((l) => l !== "") ? inlinePointLabels : []);
  // Raw text for the Point labels field. The displayed value can't be re-derived from the parsed
  // labels on every keystroke — that strips the comma the instant it's typed, so a second label can
  // never be added. Hold the raw string and only resync when the parsed value diverges externally.
  const pointLabelsCanonical = pointLabelsForField.join(", ");
  const [pointLabelsRaw, setPointLabelsRaw] = useState(pointLabelsCanonical);
  useEffect(() => {
    const parts = pointLabelsRaw.split(",").map((s) => s.trim());
    while (parts.length && parts[parts.length - 1] === "") parts.pop();
    if (parts.join(", ") !== pointLabelsCanonical) setPointLabelsRaw(pointLabelsCanonical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointLabelsCanonical]);
  // polygon "sketch several rectangles" mode: target_areas holds one area per rectangle.
  const multiRect = kind === "polygon" && Array.isArray(safeSpec.target_areas) && safeSpec.target_areas.length > 0;
  const [bbox, setBbox] = useState({ x: "0.1", y: "0.08", w: "0.8", h: "0.84", rows: "4", cols: "4", sides: "10", rot: "0", inner: "0.72", topSkip: "0", unit: "1", polyRot: "", dx: "", dy: "", r: "0.035" });
  const [editTarget, setEditTarget] = useState("answer"); // drag-shape: 'answer' | 'original'
  const [moveMode, setMoveMode] = useState(false); // drag the whole grid on the image instead of tapping
  // The outline the author traces on a closed edge shape. Editor-only: the grader derives the correct
  // outline from target_points, and persisting it would leak the answer to the student (it is not in the
  // backend's student-spec strip list). It exists so the canvas can render the edges as they're drawn.
  // Re-opening a saved question starts with no stored edges, which rendered the answer as bare dots —
  // seed the ring from target_points (stored in outline order) so the admin sees the finished shape.
  const [editorEdges, setEditorEdges] = useState(() => {
    if (!["polygon", "points", "drag-shape", "reflect", "rotate"].includes(kind)) return [];
    // Open lines anchored to a given point ("join K to L", K printed on the diagram): the joins ARE
    // required_edges — not a derivable outline — so seed straight from them rather than falling into the
    // closed-shape bail-out below (which would otherwise always start this case with no edges drawn).
    if (kind === "points" && spec?.connect !== false
        && ((spec?.given_points || []).length > 0 || (spec?.context_points || []).length > 0)
        && Array.isArray(spec?.required_edges) && spec.required_edges.length > 0) {
      const key = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
      return spec.required_edges
        .filter((pair) => Array.isArray(pair) && Array.isArray(pair[0]) && Array.isArray(pair[1]))
        .map(([a, b]) => [key(a), key(b)].sort().join("|"));
    }
    if (kind === "polygon" && Array.isArray(spec?.target_areas) && spec.target_areas.length > 0) return [];
    if (spec?.closed === false || (kind === "points" && spec?.connect === false)) return [];
    if (Number(spec?.shape_count) > 1) return [];
    const pts = Array.isArray(spec?.target_points) ? spec.target_points.filter((p) => Array.isArray(p) && p.length >= 2) : [];
    if (pts.length < 3) return [];
    const key = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
    return pts.map((p, i) => [key(p), key(pts[(i + 1) % pts.length])].sort().join("|"));
  });
  const [regionGen, setRegionGen] = useState(null); // active shade generator: 'rect' | 'triangle' | 'sector' | 'star' | 'proportional' | 'circles'
  const [sectorRows, setSectorRows] = useState([{ label: "", value: "", given: false }, { label: "", value: "", given: false }]); // 'proportional' generator's data-table rows
  const [transformMode, setTransformMode] = useState("shape"); // reflect/rotate: 'shape' | 'axis' | 'preview'
  const [markerMode, setMarkerMode] = useState("axis"); // marker: 'axis' | 'target'
  const [givenMode, setGivenMode] = useState(false); // shade: tap regions to flag them pre-coloured "given"
  const [previewGroupShade, setPreviewGroupShade] = useState([]); // colour-by-category: scratch preview only, never saved
  const [glyphLayer, setGlyphLayer] = useState("answer"); // glyph: edit the graded 'answer' targets or the 'optional' working-grid targets
  const rehydrated = useRef(false);
  const hasCircleRegions = (safeSpec.regions || []).some((rg) => rg && rg.r != null);

  // Re-opening a SAVED colour-by-category question: `regionGen` / `sectorRows` are local UI state that
  // start empty, so the proportional panel (and with it the "Colour by category" controls) never showed
  // and the admin couldn't edit the categories at all. Rebuild that state from the stored spec on first
  // mount: the data-table rows come back from `groups` (label + count), plus one `given` row standing for
  // the locked worked-example units. Rebuilding the rows BEFORE arming the generator matters — the
  // regeneration effect derives the regions from sectorRows, so a faithful restore regenerates the same
  // regions it just loaded instead of wiping them.
  // Circle-lattice shade (e.g. NL2014 Q30a) has the same gap: rows/cols fields existed but no generator
  // was armed, so changing them did nothing and Rect grid replaced circles with squares.
  useEffect(() => {
    if (rehydrated.current) return;
    if (kind !== "shade") return;
    if (Array.isArray(safeSpec.groups) && safeSpec.groups.length > 0) {
      rehydrated.current = true;
      // The chart's own geometry, so the bbox fields describe the saved chart rather than the defaults.
      // Without this the fields read "10 sides / rotate 0" for a chart that is really a circle starting at
      // 12 o'clock, and the first click on a generator button rebuilt it as that wrong shape.
      const geom = safeSpec.chart || inferSectorBbox(safeSpec.regions);
      const unit = Math.max(num(safeSpec.chart?.unit, 1), 1e-9);
      if (geom) setBbox((b) => ({ ...b, ...geom, unit: String(unit) }));
      const givenUnits = (safeSpec.regions || []).filter((r) => r.given).length;
      const rows = [];
      // Values are the DATA (a section may stand for more than one unit of it), which is what the admin
      // typed and what the question's own table shows — `groups[].count` is that divided by the unit.
      if (givenUnits > 0) rows.push({ label: safeSpec.given_label || "given", value: String(givenUnits * unit), given: true });
      safeSpec.groups.forEach((g) => rows.push({ label: String(g.label ?? g.key ?? ""), value: String((Number(g.count) || 0) * unit), given: false }));
      setSectorRows(rows);
      // NOTE: deliberately does NOT arm `regionGen` — the regeneration effect would rewrite the regions,
      // and even now that the bbox is faithful, a rounding difference would be an unasked-for edit. The
      // panel renders off `showProportionalPanel` instead, so the categories are editable while the saved
      // slices are left alone until the admin explicitly re-runs a generator — which now reproduces them.
      return;
    }
    const circ = (safeSpec.regions || []).filter((rg) => rg && rg.r != null);
    if (circ.length === 0) return;
    rehydrated.current = true;
    const stored = safeSpec.lattice;
    const geom = stored
      ? {
          x: String(stored.x), y: String(stored.y),
          dx: String(stored.dx), dy: String(stored.dy),
          rows: String(stored.rows), cols: String(stored.cols),
          r: String(stored.r),
          w: String(r4((Number(stored.cols) > 1 ? (Number(stored.cols) - 1) * Number(stored.dx) : Number(stored.dx)) || 0.1)),
          h: String(r4((Number(stored.rows) > 1 ? (Number(stored.rows) - 1) * Number(stored.dy) : Number(stored.dy)) || 0.1)),
        }
      : inferCircleLattice(safeSpec.regions);
    if (geom) setBbox((b) => ({ ...b, ...geom }));
    // Do not arm regionGen here — same as proportional. Circle fields still show via showCirclePanel;
    // the first edit (or an explicit "Circle lattice" click) arms the generator and live-updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The interactive can sit on a different image than the question's own (e.g. the answer is placed on
  // a map carried by the parent stem, not on this part's prompt image). Keep the spec's own image_url
  // once set — only fall back to the passed question image when the spec has none — so editing never
  // silently clobbers it.
  const bgImage = safeSpec.image_url || imageUrl || "";
  const update = (patch) => onChange({ ...safeSpec, kind, image_url: bgImage, ...patch });

  const grid = safeSpec.grid || { x0: 0.1, y0: 0.1, dx: 0.1, dy: 0.1, cols: 8, rows: 8 };
  const setGrid = (patch) => update({ grid: { ...grid, ...patch } });

  // ---- reflect / rotate: compute the transformed target vertices from the original shape ----
  const gx0 = num(grid.x0, 0.1), gy0 = num(grid.y0, 0.1), gdx = num(grid.dx, 0.1) || 0.1, gdy = num(grid.dy, 0.1) || 0.1;
  const fracToGrid = ([x, y]) => [(num(x) - gx0) / gdx, (num(y) - gy0) / gdy];
  const reflectAcross = (p, a, b) => {
    const dx = b[0] - a[0], dy = b[1] - a[1], dd = dx * dx + dy * dy || 1;
    const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / dd;
    return [Math.round(2 * (a[0] + t * dx) - p[0]), Math.round(2 * (a[1] + t * dy) - p[1])];
  };
  const rotatePt = (p, c, angle) => {
    const dx = p[0] - c[0], dy = p[1] - c[1];
    const [nx, ny] = angle === "cw90" ? [-dy, dx] : angle === "ccw90" ? [dy, -dx] : [-dx, -dy];
    return [Math.round(c[0] + nx), Math.round(c[1] + ny)];
  };
  const computeReflectTarget = (original, mirror) => {
    if (!Array.isArray(mirror) || mirror.length < 2 || !Array.isArray(original) || original.length < 1) return [];
    const a = fracToGrid(mirror[0]), b = fracToGrid(mirror[1]);
    return original.map((p) => reflectAcross([num(p[0]), num(p[1])], a, b));
  };
  const computeRotateTarget = (original, center, angle) => {
    if (!Array.isArray(center) || center.length < 2 || !Array.isArray(original) || original.length < 1) return [];
    const c = fracToGrid(center);
    return original.map((p) => rotatePt([num(p[0]), num(p[1])], c, angle || "cw90"));
  };

  // ---- clock: hand endpoints (fractional) ↔ target hour/minute ----
  const clockCenter = Array.isArray(safeSpec.center) && safeSpec.center.length >= 2 ? [num(safeSpec.center[0], 0.5), num(safeSpec.center[1], 0.5)] : [0.5, 0.5];
  const clockRadius = num(safeSpec.radius, 0.4) || 0.4;
  const clockHandEndpoints = (hour, minute) => {
    const hb = (hour % 12) * 30 + minute * 0.5, mb = minute * 6;
    const ep = (bearing, r) => [clockCenter[0] + Math.sin((bearing * Math.PI) / 180) * r, clockCenter[1] - Math.cos((bearing * Math.PI) / 180) * r];
    return [ep(hb, clockRadius * 0.55), ep(mb, clockRadius * 0.92)];
  };
  const bearingOf = (p) => {
    const deg = (Math.atan2(num(p[0]) - clockCenter[0], -(num(p[1]) - clockCenter[1])) * 180) / Math.PI;
    return (deg + 360) % 360;
  };

  // ---- marker: project a fractional point onto the axis → value ----
  const projectValue = (p, axis) => {
    if (!Array.isArray(axis) || axis.length < 2) return null;
    const a = [num(axis[0][0]), num(axis[0][1])], b = [num(axis[1][0]), num(axis[1][1])];
    const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy || 1;
    let t = ((num(p[0]) - a[0]) * dx + (num(p[1]) - a[1]) * dy) / l2;
    t = Math.max(0, Math.min(1, t));
    return { t, point: [a[0] + t * dx, a[1] + t * dy] };
  };

  // Colour-by-category is ON when the admin ticks it, or when the loaded spec already has `groups` —
  // the saved spec carries no `colour_by_group` flag of its own (the grader keys off `groups`), so
  // without this an existing question would reopen with the flag false and the regeneration effect
  // would take the wrong branch and wipe its `groups`.
  const colourByGroup = !!safeSpec.colour_by_group || (Array.isArray(safeSpec.groups) && safeSpec.groups.length > 0);
  // Show the sector data-table panel while a generator is armed, OR whenever the question already IS a
  // colour-by-category one — otherwise re-opening a saved question showed no way to edit its categories.
  const showProportionalPanel = regionGen === "proportional" || colourByGroup;
  // Same for circle lattices: re-opened specs leave regionGen null until the admin edits or clicks
  // "Circle lattice", but the dx/dy/r fields and button highlight must still show.
  const showCirclePanel = regionGen === "circles" || (hasCircleRegions && !regionGen);
  const setCircleBbox = (patch) => {
    if (regionGen !== "circles") setRegionGen("circles");
    setBbox((b) => ({ ...b, ...patch }));
  };
  // Editing a category when no generator is armed must NOT rebuild the regions (that would replace the
  // authored geometry) — just rewrite `groups` from the table, which is where the answer lives.
  const commitSectorRows = (rows) => {
    setSectorRows(rows);
    if (regionGen !== "proportional" && colourByGroup) {
      const givenRow = rows.find((r) => r.given && String(r.label || "").trim() !== "");
      update({
        groups: genSectorGroups(rows, bbox.unit),
        ...(givenRow ? { given_label: String(givenRow.label).trim() } : {}),
      });
    }
  };

  // Live preview: once a shade generator is chosen, regenerate the regions whenever the bounding-box
  // fields (x/y/w/h/rows/cols/sides/rot) change, so the grid updates as the admin types — no re-click.
  useEffect(() => {
    if (kind !== "shade" || !regionGen) return;
    const regions = regionGen === "rect" ? genRectRegions(bbox)
      : regionGen === "circles" ? genCircleLatticeRegions(bbox)
      : regionGen === "triangle" ? genTriangleRegions(bbox)
      : regionGen === "star" ? genStarRegions(bbox)
      : regionGen === "proportional" ? genProportionalSectorRegions(sectorRows, bbox, !!safeSpec.label_only, colourByGroup)
      : genSectorRegions(bbox);
    const patch = { regions };
    if (regionGen === "circles") {
      patch.lattice = latticePatchFromBbox(bbox);
      const prevLat = safeSpec.lattice || inferCircleLattice(safeSpec.regions);
      if (prevLat && (Number(prevLat.rows) !== patch.lattice.rows || Number(prevLat.cols) !== patch.lattice.cols)) {
        patch.target_regions = [];
        if (safeSpec.target_mode === "polyomino") patch.target_shape = [];
      }
    }
    if ((regionGen === "sector" || regionGen === "star") && !safeSpec.target_mode) patch.target_mode = "count";
    if (regionGen === "proportional") {
      // Remember how this chart was drawn, so re-opening it restores the real bounding box / boundary /
      // start angle / unit instead of the defaults — the regions alone don't say which of the many
      // (bbox, sides, rot) combinations produced them.
      patch.chart = {
        x: num(bbox.x, 0.1), y: num(bbox.y, 0.08), w: num(bbox.w, 0.8), h: num(bbox.h, 0.84),
        sides: Math.round(num(bbox.sides, 0)), rot: num(bbox.rot, -90), unit: Math.max(num(bbox.unit, 1), 1e-9),
        ...(String(bbox.polyRot).trim() !== "" ? { polyRot: num(bbox.polyRot, 0) } : {}),
      };
      if (colourByGroup) {
        // Colour-by-category: the chart is cut into unit regions and the student colours the right
        // NUMBER of them per category. The answer lives in `groups[].count`
        // (InteractiveDiagramGrading::gradeInteractiveShadeGroups) — nothing is target_region-based.
        patch.groups = genSectorGroups(sectorRows, bbox.unit);
        patch.cyclic = true;
        patch.target_regions = [];
        patch.require_labels = false;
        patch.label_only = false;
        // The given row's label ("sleeping") isn't a category, so it has nowhere else to live — persist
        // it so re-opening the question rebuilds the data table with its real name, not a placeholder.
        const givenRow = sectorRows.find((r) => r.given && String(r.label || "").trim() !== "");
        if (givenRow) patch.given_label = String(givenRow.label).trim();
      } else if (safeSpec.label_only) {
        // Label-only: nothing is tappable/targeted — grading is purely the typed labels
        // (InteractiveDiagramGrading::gradeShadeLabelsOnly), so target_regions stays empty.
        patch.groups = undefined;
        patch.target_regions = [];
      } else {
        patch.groups = undefined;
        // "Complete the chart" semantics: every generated sector is required, so pre-select them all
        // instead of making the admin tap each one — and the typed-label check is on by default.
        if (!safeSpec.target_mode) patch.target_mode = "exact";
        patch.target_regions = regions.map((r) => r.id);
        if (safeSpec.require_labels === undefined) patch.require_labels = true;
      }
    } else if ((safeSpec.regions || []).length !== regions.length) {
      patch.target_regions = [];
      if (safeSpec.target_mode === "polyomino") patch.target_shape = [];
    }
    update(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionGen, bbox.x, bbox.y, bbox.w, bbox.h, bbox.rows, bbox.cols, bbox.sides, bbox.rot, bbox.inner, bbox.topSkip, bbox.unit, bbox.polyRot, bbox.dx, bbox.dy, bbox.r, sectorRows, safeSpec.label_only, colourByGroup]);

  // Drag the whole grid: for 'shade' shift every region polygon; for grid-based kinds shift the
  // grid origin (x0/y0). Deltas are fractions of the image, matching the canvas coordinate space.
  const moveGrid = (dfx, dfy) => {
    if (kind === "shade") {
      const regions = (safeSpec.regions || []).map((rg) => (
        rg.r != null
          ? { ...rg, cx: num(rg.cx) + dfx, cy: num(rg.cy) + dfy }
          : { ...rg, points: (rg.points || []).map(([x, y]) => [num(x) + dfx, num(y) + dfy]) }
      ));
      update({ regions });
    } else {
      update({ grid: { ...grid, x0: num(grid.x0, 0.1) + dfx, y0: num(grid.y0, 0.1) + dfy } });
    }
  };
  // shade given-mode: flag/unflag a region as pre-coloured. A given cell can't also be a target.
  const toggleGiven = (id) => {
    const sid = String(id);
    const regions = (safeSpec.regions || []).map((r) => (String(r.id) === sid ? { ...r, given: !r.given } : r));
    const nowGiven = !!regions.find((r) => String(r.id) === sid)?.given;
    const patch = { regions };
    if (nowGiven && Array.isArray(safeSpec.target_regions)) {
      patch.target_regions = safeSpec.target_regions.filter((t) => String(t) !== sid);
    }
    update(patch);
  };
  const canMoveGrid = kind === "shade" ? (safeSpec.regions || []).length > 0
    : (kind === "lines" || kind === "cut-drag" || kind === "glyph" || kind === "clock" || kind === "marker" || kind === "pictogram") ? false
    : (kind === "reflect" || kind === "rotate") ? transformMode === "shape"
    : true;
  const noSymmetry = kind === "lines" && safeSpec.target_count === 0;

  // Canvas value/onChange wiring depends on kind + what is being edited.
  let canvasValue, canvasOnChange, canvasSpec;
  if (kind === "shade") {
    // Pass the colour-by-category fields through so the preview renders exactly what the student sees:
    // the category picker, per-category fill colours and the sector captions.
    canvasSpec = { kind, regions: safeSpec.regions || [] };
    if (colourByGroup && Array.isArray(safeSpec.groups) && safeSpec.groups.length) {
      canvasSpec.groups = safeSpec.groups;
      canvasSpec.cyclic = safeSpec.cyclic !== false;
    }
    if (safeSpec.target_mode === "polyomino") {
      // "Draw pattern N": tapped cells define the fixed shape (target_shape = [col,row] list), matched
      // anywhere on the grid. Selection is stored as cells, not region ids.
      const cellKey = (c) => `${c[0]},${c[1]}`;
      const shapeSet = new Set((safeSpec.target_shape || []).map(cellKey));
      canvasValue = (safeSpec.regions || []).filter((r) => r.cell && shapeSet.has(cellKey(r.cell))).map((r) => String(r.id));
      canvasOnChange = (ids) => {
        const byId = new Map((safeSpec.regions || []).map((r) => [String(r.id), r.cell]));
        const cells = ids.map((id) => byId.get(String(id))).filter(Boolean).map((c) => [Number(c[0]), Number(c[1])]);
        update({ target_shape: cells });
      };
    } else if (canvasSpec.groups) {
      // Colour-by-category: there is no target_regions to author — the answer is the per-category
      // `count` taken from the data table. The preview is a scratch surface (the admin can colour it in
      // to sanity-check the layout); its value is deliberately NOT written back into the spec, or the
      // canvas's {id, group} entries would corrupt target_regions.
      canvasValue = previewGroupShade;
      canvasOnChange = setPreviewGroupShade;
    } else {
      canvasValue = (safeSpec.target_regions || []).map(String);
      canvasOnChange = (ids) => update({ target_regions: ids.map((v) => (Number.isNaN(Number(v)) ? v : Number(v))) });
    }
  } else if (kind === "rect") {
    canvasSpec = { kind: "rect", grid };
    canvasValue = safeSpec.target_points || [];
    canvasOnChange = (pts) => update({ target_points: pts });
  } else if (kind === "cut-drag") {
    // The shape polygon comes from the spec (authored at import time); here the admin taps two
    // points to set the model cut. Only its DIRECTION is graded — position is free for the student.
    canvasSpec = { kind: "cut-drag", shape: safeSpec.shape || [], snap_tol: safeSpec.snap_tol };
    canvasValue = Array.isArray(safeSpec.target_cut) ? { cut: safeSpec.target_cut, offsets: [[0, 0], [0, 0]] } : null;
    canvasOnChange = (v) => update({ target_cut: v && Array.isArray(v.cut) ? v.cut : null });
  } else if (kind === "lines") {
    canvasSpec = { kind: "lines" };
    canvasValue = safeSpec.target_lines || [];
    canvasOnChange = (lines) => update({ target_lines: lines, target_count: lines.length });
  } else if (kind === "polygon") {
    // allow_labels (not require_point_labels) while authoring: the admin types each corner's letter in
    // the inline box on the grid — the forced prompt is the student's entry path, not the author's.
    // point_count is the STUDENT's tap budget (the corners they must add); the author taps every corner
    // of the model shape, printed ones included, so the editor canvas is left uncapped when some corners
    // are given. given_points render as the grey, joinable corners the student will see.
    canvasSpec = multiRect
      ? { kind: "polygon", grid, require_shape: "rectangle", shape_count: safeSpec.shape_count || (safeSpec.target_areas || []).length || 1, point_count: safeSpec.point_count || ((safeSpec.target_areas || []).length * 4) }
      : {
        kind: "polygon",
        grid,
        point_count: (safeSpec.given_points || []).length ? 0 : (safeSpec.point_count || safeSpec.require_vertices || 0),
        point_labels: pointLabelsForField,
        allow_labels: safeSpec.allow_labels !== false,
      };
    canvasValue = multiRect
      ? (safeSpec.target_points || [])
      : { points: safeSpec.target_points || [], edges: editorEdges };
    canvasOnChange = (pts) => {
      const targets = Array.isArray(pts) ? pts : (pts?.points || []);
      if (!Array.isArray(pts) && Array.isArray(pts?.edges)) setEditorEdges(pts.edges);
      const givenCount = (safeSpec.given_points || []).length;
      // Keep the student's tap budget in step with the shape: they add every corner that isn't already
      // printed. Re-derived here as well as in the given-corners field, so the two can be authored in
      // either order.
      update({
        target_points: targets,
        ...(givenCount && targets.length > givenCount ? { point_count: targets.length - givenCount } : {}),
      });
    };
  } else if (kind === "glyph") {
    const useGrid = safeSpec.use_grid !== false;
    const editingOptional = glyphLayer === "optional";
    // Pass symbol_palette so the authoring canvas shows the same tap-a-symbol buttons the student gets —
    // otherwise non-typeable glyphs (△ ▽ ○ ↔) can't be placed here at all.
    canvasSpec = { kind: "glyph", grid: useGrid ? grid : null, symbol_palette: safeSpec.symbol_palette || undefined };
    canvasValue = (editingOptional ? safeSpec.optional_targets : safeSpec.targets) || [];
    canvasOnChange = (glyphs) => update(editingOptional
      ? { optional_targets: glyphs }
      : { targets: glyphs, grid: useGrid ? grid : undefined, use_grid: useGrid });
  } else if (kind === "reflect" || kind === "rotate") {
    const original = safeSpec.original_shape || [];
    const recompute = (orig, over = {}) => {
      const m = "mirror" in over ? over.mirror : safeSpec.mirror;
      const c = "center" in over ? over.center : safeSpec.center;
      const ang = over.angle || safeSpec.angle;
      return kind === "reflect" ? computeReflectTarget(orig, m) : computeRotateTarget(orig, c, ang);
    };
    if (transformMode === "shape") {
      // Author the source shape as plain, open points (connect:false) so the canvas stays a bare point
      // array here — the {points,edges} value shape is only for the graded student outline, not authoring.
      canvasSpec = { kind: "points", grid, connect: false, closed: false, point_count: 0, allow_labels: false };
      canvasValue = original;
      canvasOnChange = (pts) => { const a = Array.isArray(pts) ? pts : (pts?.points || []); update({ original_shape: a, point_count: a.length, target_points: recompute(a) }); };
    } else if (transformMode === "axis" && kind === "reflect") {
      canvasSpec = { kind: "lines" };
      canvasValue = safeSpec.mirror ? [safeSpec.mirror] : [];
      canvasOnChange = (lines) => { const mirror = lines.length ? lines[lines.length - 1] : null; update({ mirror, target_points: recompute(original, { mirror }) }); };
    } else if (transformMode === "axis" && kind === "rotate") {
      canvasSpec = { kind: "points", grid, connect: false, point_count: 1, allow_labels: false };
      canvasValue = safeSpec.center ? [fracToGrid(safeSpec.center).map(Math.round)] : [];
      canvasOnChange = (pts) => { const g = pts.length ? pts[pts.length - 1] : null; const center = g ? [gx0 + g[0] * gdx, gy0 + g[1] * gdy] : null; update({ center, target_points: recompute(original, { center }) }); };
    } else {
      canvasSpec = { kind, grid, original_shape: original, mirror: safeSpec.mirror, center: safeSpec.center, connect: safeSpec.connect !== false, closed: safeSpec.closed !== false, point_count: original.length, allow_labels: false };
      canvasValue = safeSpec.closed !== false
        ? { points: safeSpec.target_points || [], edges: editorEdges }
        : (safeSpec.target_points || []);
      canvasOnChange = (pts) => {
        if (!Array.isArray(pts) && Array.isArray(pts?.edges)) setEditorEdges(pts.edges);
        update({ target_points: Array.isArray(pts) ? pts : (pts?.points || []) });
      };
    }
  } else if (kind === "segments") {
    const segNodes = Array.isArray(safeSpec.nodes) ? safeSpec.nodes : [];
    const nearest = (p) => {
      let bi = -1, bd = 0.06;
      segNodes.forEach((n, i) => { const d = Math.hypot(num(p[0]) - num(n[0]), num(p[1]) - num(n[1])); if (d <= bd) { bd = d; bi = i; } });
      return bi;
    };
    canvasSpec = { kind: "segments", nodes: segNodes };
    canvasValue = (safeSpec.target_segments || []).map(([i, j]) => (segNodes[i] && segNodes[j] ? [segNodes[i], segNodes[j]] : null)).filter(Boolean);
    canvasOnChange = (segs) => {
      const pairs = [];
      (segs || []).forEach((s) => {
        const a = nearest(s[0]), b = nearest(s[1]);
        if (a >= 0 && b >= 0 && a !== b) { const p = a < b ? [a, b] : [b, a]; if (!pairs.some((q) => q[0] === p[0] && q[1] === p[1])) pairs.push(p); }
      });
      update({ target_segments: pairs });
    };
  } else if (kind === "clock") {
    const hour = Math.round(num(safeSpec.target_hour, 3)) % 12, minute = Math.round(num(safeSpec.target_minute, 0)) % 60;
    canvasSpec = { kind: "clock", center: clockCenter, radius: clockRadius };
    canvasValue = clockHandEndpoints(hour, minute);
    canvasOnChange = (hands) => {
      if (!Array.isArray(hands) || hands.length < 2) return;
      const nh = ((Math.floor(bearingOf(hands[0]) / 30) % 12) + 12) % 12;
      const nm = ((Math.round(bearingOf(hands[1]) / 6) % 60) + 60) % 60;
      update({ target_hour: nh, target_minute: nm });
    };
  } else if (kind === "marker") {
    const axis = safeSpec.axis || null;
    if (markerMode === "axis" || !axis) {
      canvasSpec = { kind: "lines" };
      canvasValue = axis ? [axis] : [];
      canvasOnChange = (lines) => update({ axis: lines.length ? lines[lines.length - 1] : null });
    } else {
      canvasSpec = { kind: "marker", axis, style: safeSpec.style || "arrow", bar_width: safeSpec.bar_width, marker_label: safeSpec.marker_label };
      // target_point may be null on an imported question that only stores target_value — project it back
      // onto the axis so the author SEES the current answer instead of a blank scale.
      canvasValue = safeSpec.target_point
        ? [safeSpec.target_point]
        : (safeSpec.target_value != null && axis
            ? (() => {
                const v0 = num(safeSpec.v0, 0), v1 = num(safeSpec.v1, 10);
                const t = v1 === v0 ? 0 : Math.max(0, Math.min(1, (num(safeSpec.target_value, 0) - v0) / (v1 - v0)));
                const [a, b] = axis;
                return [[num(a[0]) + t * (num(b[0]) - num(a[0])), num(a[1]) + t * (num(b[1]) - num(a[1]))]];
              })()
            : []);
      canvasOnChange = (pts) => {
        const p = Array.isArray(pts) && pts.length ? pts[pts.length - 1] : null;
        if (!p) return update({ target_point: null, target_value: null });
        const proj = projectValue(p, axis);
        const v0 = num(safeSpec.v0, 0), v1 = num(safeSpec.v1, 10);
        update({ target_point: proj.point, target_value: Math.round((v0 + proj.t * (v1 - v0)) * 1000) / 1000 });
      };
    }
  } else if (kind === "pictogram") {
    const row = safeSpec.row || { x0: 0.4, y: 0.85, dx: 0.06, count: 6 };
    canvasSpec = { kind: "pictogram", row, icon: safeSpec.icon || "bottle" };
    const t = num(safeSpec.target_icons, 0);
    const full = Math.floor(t + 1e-9), frac = Math.round((t - full) * 4) / 4;
    canvasValue = Array.from({ length: Math.max(0, Math.round(num(row.count, 0))) }, (_, i) => (i < full ? 1 : i === full ? frac : 0));
    canvasOnChange = (states) => update({ row, target_icons: (states || []).reduce((s, v) => s + (Number(v) || 0), 0) });
  } else {
    // given_points (corners PRINTED on the figure) and no_new_points ("join them, don't plot any") were
    // never forwarded: an edges-only question showed the author a blank grid with no corners to join, and
    // an empty-cell tap planted a stray corner into target_points that the grader then rejects.
    canvasSpec = { kind, grid, connect: safeSpec.connect !== false, closed: safeSpec.closed !== false, shape_count: safeSpec.shape_count || 1, point_labels: pointLabelsForField, point_count: safeSpec.point_count || 0, original_shape: safeSpec.original_shape || [], allow_labels: safeSpec.allow_labels !== false, given_points: safeSpec.given_points || undefined, context_points: safeSpec.context_points || undefined, no_new_points: safeSpec.no_new_points || undefined, required_edges: safeSpec.required_edges || undefined };
    // On a closed edge-drawing shape the canvas works in {points, edges} and re-reads the edges it just
    // drew back out of `value` on every render. Feeding it a bare point array (and dropping `edges` on
    // the way in) wiped the outline as fast as it was drawn — a corner tap armed the ×, but the join
    // never stuck and the shape could never be closed.
    //
    // The author's outline is NOT persisted into the spec: the grader rebuilds the correct outline from
    // target_points itself (transformOutlineClosed), and an extra target_edges key isn't in the backend's
    // student-spec strip list, so storing it would ship the answer's outline to the student. Keep the
    // edges in local state purely so the editor canvas can draw them.
    if (kind === "drag-shape" && editTarget === "original") {
      canvasValue = safeSpec.original_shape || [];
      canvasOnChange = (pts) => update({ original_shape: Array.isArray(pts) ? pts : (pts?.points || []) });
      canvasSpec = { ...canvasSpec, kind: "points" }; // edit original via point taps
    } else {
      canvasValue = { points: safeSpec.target_points || [], edges: editorEdges };
      // The outline is only derivable from target_points when the student plots EVERY corner. As soon as
      // some corners are printed on the figure (given_points / context_points) the joins are the graded
      // answer and must persist as required_edges — that is true of a closed shape too (the kite whose
      // three corners are printed and only one is plotted), so this must not gate on `closed === false`.
      const contextPointsForEdges = kind === "points" && safeSpec.connect !== false
        ? [...(safeSpec.given_points || []), ...(safeSpec.context_points || [])]
        : [];
      canvasOnChange = (pts) => {
        if (Array.isArray(pts)) {
          update({ target_points: pts });
          return;
        }
        const nextEdges = Array.isArray(pts?.edges) ? pts.edges : [];
        setEditorEdges(nextEdges);
        const nextPoints = pts?.points || [];
        const patch = { target_points: nextPoints };
        // Open lines anchored to a given point: the drawn edges ARE the graded answer (required_edges),
        // not a derivable outline like the closed-shape case above — persist them as coordinate pairs
        // (either end may be a plotted point or one of the given context points) or the join the admin
        // just drew would vanish again on save/reload.
        if (contextPointsForEdges.length > 0) {
          const key = (p) => `${Math.round(Number(p[0]))},${Math.round(Number(p[1]))}`;
          const byKey = new Map([...nextPoints, ...contextPointsForEdges].map((p) => [key(p), [p[0], p[1]]]));
          patch.required_edges = nextEdges
            .map((k) => k.split("|"))
            .map(([a, b]) => [byKey.get(a), byKey.get(b)])
            .filter(([a, b]) => a && b);
        }
        update(patch);
      };
      if (kind === "drag-shape") canvasSpec = { ...canvasSpec, kind: "points" };
    }
  }

  // Point labels + "the label is graded" — shared by the plot-point kinds and by polygon ("draw kite
  // ABCD, labelling B and D": the shape is only half the answer when the question names its corners).
  const pointLabelsBlock = (
    <>
      <TextField
        size="small"
        fullWidth
        label="Point labels (comma-separated, in tap order — e.g. A, B, C)"
        value={pointLabelsRaw}
        onChange={(e) => {
          setPointLabelsRaw(e.target.value);
          // Preserve positional empties: labels map to target_points by index, so a blank
          // entry (an unlabelled point among labelled ones, e.g. "B, , C") must be kept.
          const parts = e.target.value.split(",").map((s) => s.trim());
          while (parts.length && parts[parts.length - 1] === "") parts.pop();
          update({ point_labels: parts });
        }}
        helperText="Optional default labels, positional by tap order — leave a slot blank (e.g. “, B, , D”) for a corner with no label, e.g. one already printed on the diagram. Students can also type a label on each point (the typed label wins)."
      />
      {pointLabelsForField.length > 0 && (
        <FormControlLabel
          control={<Checkbox size="small" checked={!!safeSpec.require_point_labels} onChange={(e) => update({ require_point_labels: e.target.checked, point_labels: pointLabelsForField })} />}
          label={<Typography variant="caption">Require the label to be typed (e.g. "plot C" — the point must be at the right spot AND labelled "C"). Only the points you name above are graded on their label.</Typography>}
        />
      )}
      {/* The question names the points ("Mark the points A, B and C, labelling each") and the
          labels ARE authored on the target points — but without this flag the grader ignores them
          entirely, so a wrong or missing label still marks correct. Surface it. */}
      {pointLabelsForField.length > 0 && !safeSpec.require_point_labels && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          This question has labels ({pointLabelsForField.filter(Boolean).join(", ")}) but they are
          <strong> not graded</strong> — a student who plots the right spots with the wrong labels (or no
          labels) is still marked correct. Tick “Require the label to be typed” above if the question
          asks the student to label the points.
        </Alert>
      )}
      {/* Labels are positional. Typing one letter ("D") for a 4-corner shape used to pin that
          letter to the FIRST corner, so no student answer could ever be marked correct. Grading
          now prefers a label stored on the target point itself, but the mismatch is still a
          mis-authoring — surface it here so it gets fixed at the source. */}
      {!!safeSpec.require_point_labels
        && pointLabelsForField.length > 0
        && (safeSpec.point_count || 0) > 0
        && pointLabelsForField.length !== (safeSpec.point_count || 0)
        && !(safeSpec.target_points || []).some((p) => Array.isArray(p) && typeof p[2] === "string" && p[2].trim() !== "") && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {pointLabelsForField.length} label(s) for {safeSpec.point_count} points. Labels map to
          points <strong>by position</strong>, so “{pointLabelsForField.join(", ")}” names the
          first {pointLabelsForField.length}. To label only a later point, leave the earlier slots
          blank (e.g. “, , , D”) — or set the label on the point itself in the “Correct placement” grid.
        </Alert>
      )}
    </>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <TextField
        size="small"
        fullWidth
        multiline
        label="Custom instructions shown to the student (optional)"
        value={safeSpec.instructions || ""}
        onChange={(e) => update({ instructions: e.target.value || undefined })}
        helperText="Overrides the generic per-kind hint (“Tap the diagram…”) — use it to spell out multi-step tasks, e.g. “Tap K, then L, to join them with a line.”"
      />
      {kind === "shade" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Generate the regions students can tap, then tap the correct ones below.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              label={showCirclePanel ? "x0 (centre)" : "x"}
              type="number"
              value={bbox.x}
              onChange={(e) => (showCirclePanel ? setCircleBbox({ x: e.target.value }) : setBbox({ ...bbox, x: e.target.value }))}
              sx={{ width: 100 }}
              inputProps={{ step: 0.01 }}
            />
            <TextField
              size="small"
              label={showCirclePanel ? "y0 (centre)" : "y"}
              type="number"
              value={bbox.y}
              onChange={(e) => (showCirclePanel ? setCircleBbox({ y: e.target.value }) : setBbox({ ...bbox, y: e.target.value }))}
              sx={{ width: 100 }}
              inputProps={{ step: 0.01 }}
            />
            {!showCirclePanel && (
              <>
                <TextField size="small" label="w" type="number" value={bbox.w} onChange={(e) => setBbox({ ...bbox, w: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
                <TextField size="small" label="h" type="number" value={bbox.h} onChange={(e) => setBbox({ ...bbox, h: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
              </>
            )}
            {showCirclePanel && (
              <>
                <TextField size="small" label="dx" type="number" value={bbox.dx} onChange={(e) => setCircleBbox({ dx: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.001 }} />
                <TextField size="small" label="dy" type="number" value={bbox.dy} onChange={(e) => setCircleBbox({ dy: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.001 }} />
                <TextField size="small" label="radius" type="number" value={bbox.r} onChange={(e) => setCircleBbox({ r: e.target.value })} sx={{ width: 90 }} inputProps={{ step: 0.001, min: 0.005 }} />
              </>
            )}
            <TextField
              size="small"
              label="rows"
              type="number"
              value={bbox.rows}
              onChange={(e) => (showCirclePanel ? setCircleBbox({ rows: e.target.value }) : setBbox({ ...bbox, rows: e.target.value }))}
              sx={{ width: 80 }}
            />
            <TextField
              size="small"
              label="cols"
              type="number"
              value={bbox.cols}
              onChange={(e) => (showCirclePanel ? setCircleBbox({ cols: e.target.value }) : setBbox({ ...bbox, cols: e.target.value }))}
              sx={{ width: 80 }}
            />
            <Tooltip title="Place rectangular cells over the box (x,y,w,h as fractions of the image). Edits to x/y/w/h/rows/cols then preview live.">
              <Button size="small" variant={regionGen === "rect" ? "contained" : "outlined"} onClick={() => setRegionGen("rect")}>Rect grid</Button>
            </Tooltip>
            <Tooltip title="Rows×cols lattice of tappable circles (cx/cy/r) — for 'complete the pattern' diagrams. x0/y0 = first centre; dx/dy = spacing; then tap the filled circles below. Changing rows/cols live-updates the lattice.">
              <Button size="small" variant={showCirclePanel ? "contained" : "outlined"} onClick={() => setRegionGen("circles")}>Circle lattice</Button>
            </Tooltip>
            <Tooltip title="Subdivide an equilateral triangle (apex top-centre) into rows of small triangles">
              <Button size="small" variant={regionGen === "triangle" ? "contained" : "outlined"} onClick={() => setRegionGen("triangle")}>Triangle grid</Button>
            </Tooltip>
            {regionGen === "triangle" && (
              <Tooltip title="Omit this many pointed rows from the top — use when the picture's apex is cropped so no grid sits above the shape.">
                <TextField size="small" label="skip top rows" type="number" value={bbox.topSkip} onChange={(e) => setBbox({ ...bbox, topSkip: e.target.value })} sx={{ width: 110 }} inputProps={{ min: 0 }} />
              </Tooltip>
            )}
            <TextField size="small" label="sides" type="number" value={bbox.sides} onChange={(e) => setBbox({ ...bbox, sides: e.target.value })} sx={{ width: 80 }} inputProps={{ min: 3 }} />
            <TextField size="small" label="rotate°" type="number" value={bbox.rot} onChange={(e) => setBbox({ ...bbox, rot: e.target.value })} sx={{ width: 90 }} />
            <Tooltip title="Split a regular polygon (bounding box x,y,w,h) into `sides` equal sectors from the centre — e.g. decagon → shade N sectors. Pairs with Marking: Any N cells.">
              <Button size="small" variant={regionGen === "sector" ? "contained" : "outlined"} onClick={() => setRegionGen("sector")}>Polygon sectors</Button>
            </Tooltip>
            <TextField size="small" label="inner" type="number" value={bbox.inner} onChange={(e) => setBbox({ ...bbox, inner: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.02, min: 0.1, max: 0.95 }} />
            <Tooltip title="N-pointed star (`sides` points) split into 2N small triangles — e.g. 7-point star → 14 triangles, shade 3/7 = 6. `inner` = valley radius ÷ tip radius. Pairs with Marking: Any N cells.">
              <Button size="small" variant={regionGen === "star" ? "contained" : "outlined"} onClick={() => setRegionGen("star")}>Star triangles</Button>
            </Tooltip>
            <Tooltip title="Unequal sectors sized from a data table (value ÷ total × 360°) — e.g. 'complete the pie chart'. Every row is required and pre-selected; pairs with the typed-label option below. 'rotate°' = start angle (0 = 3 o'clock, -90 = 12 o'clock, clockwise); 'sides' shapes the boundary (e.g. 12 for a clock-style 12-gon, blank/large ≈ circle) with a vertex at the start angle.">
              <Button size="small" variant={regionGen === "proportional" ? "contained" : "outlined"} onClick={() => setRegionGen("proportional")}>Proportional sectors</Button>
            </Tooltip>
          </Box>
          {showCirclePanel && (
            <Typography variant="caption" color="text.secondary">
              Circle lattice: {(safeSpec.regions || []).length} positions
              {` (${bbox.rows}×${bbox.cols})`} — edit rows/cols/dx/dy/radius to grow the grid (e.g. add a left column or bottom row), then tap the correct filled circles.
            </Typography>
          )}
          {showProportionalPanel && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <Typography variant="caption" color="text.secondary">Data table rows (label + value) — sector angle = value ÷ total × 360°:</Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Tooltip title="How much of the data ONE tappable section stands for. Leave at 1 to get one section per unit of value (8 hours = 8 slices). Set it to the highest common factor when the values are big: Whitgift's 100 boys (15/25/50/10) at 5 boys per section = 20 sections of 18°, which the child can actually tap. Values must divide by it exactly.">
                  <TextField
                    size="small" label="Value per section" type="number" value={bbox.unit}
                    onChange={(e) => setBbox({ ...bbox, unit: e.target.value })}
                    sx={{ width: 150 }} inputProps={{ min: 0 }}
                  />
                </Tooltip>
                <Tooltip title="Only for an n-gon boundary (sides ≥ 3): the angle its CORNERS sit at, when they don't line up with the first sector — e.g. a printed 12-gon chart with corners at 15° whose first slice starts at 255.5°. Leave blank to put a corner on the first sector's edge.">
                  <TextField
                    size="small" label="boundary°" type="number" value={bbox.polyRot}
                    onChange={(e) => setBbox({ ...bbox, polyRot: e.target.value })}
                    sx={{ width: 110 }} disabled={Math.round(num(bbox.sides, 0)) < 3}
                  />
                </Tooltip>
                {(() => {
                  const u = Math.max(num(bbox.unit, 1), 1e-9);
                  const bad = sectorRows.filter((r) => String(r.label || "").trim() !== "" && num(r.value, 0) > 0 && Math.abs(num(r.value, 0) / u - Math.round(num(r.value, 0) / u)) > 1e-6);
                  return bad.length ? (
                    <Typography variant="caption" color="error">
                      {bad.map((r) => r.label).join(", ")} {bad.length > 1 ? "do" : "does"} not divide into sections of {bbox.unit} — the slices would not match the data.
                    </Typography>
                  ) : null;
                })()}
              </Box>
              {sectorRows.map((row, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    size="small" label="Label" value={row.label}
                    onChange={(e) => commitSectorRows(sectorRows.map((r, j) => (j === i ? { ...r, label: e.target.value } : r)))}
                    sx={{ width: 180 }}
                  />
                  <TextField
                    size="small" label="Value" type="number" value={row.value}
                    onChange={(e) => commitSectorRows(sectorRows.map((r, j) => (j === i ? { ...r, value: e.target.value } : r)))}
                    sx={{ width: 100 }}
                  />
                  <Tooltip title="Already shown shaded+labelled in the background image (e.g. a worked example) — still consumes its angular share, but isn't tappable and isn't required in the student's answer.">
                    <FormControlLabel
                      control={<Checkbox size="small" checked={!!row.given} onChange={(e) => commitSectorRows(sectorRows.map((r, j) => (j === i ? { ...r, given: e.target.checked } : r)))} />}
                      label="Given"
                    />
                  </Tooltip>
                  <Button size="small" color="error" disabled={sectorRows.length <= 1} onClick={() => commitSectorRows(sectorRows.filter((_, j) => j !== i))}>Remove</Button>
                </Box>
              ))}
              <Button size="small" variant="outlined" sx={{ alignSelf: "flex-start" }} onClick={() => commitSectorRows([...sectorRows, { label: "", value: "", given: false }])}>Add row</Button>
              <Tooltip title="'Complete the pie chart': the chart is cut into one region per UNIT (a row's value = how many units it takes, e.g. 8 hours = 8 one-hour slices). The student picks a category and taps that many slices to colour them in — working the size out from the data table IS the task. Each category gets its own colour and its name is drawn on the sector. Rows ticked 'Given' are the worked example already printed on the chart (locked, not a category to produce). Graded on each category's slice COUNT and that its slices are contiguous.">
                <FormControlLabel
                  control={<Checkbox checked={colourByGroup} onChange={(e) => update({ colour_by_group: e.target.checked, ...(e.target.checked ? {} : { groups: undefined, given_label: undefined }) })} />}
                  label="Colour by category — student colours each activity's share (one region per unit)"
                />
              </Tooltip>
              {colourByGroup && (
                <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                  {(safeSpec.regions || []).length} unit regions
                  {(safeSpec.regions || []).filter((r) => r.given).length > 0 && ` (${(safeSpec.regions || []).filter((r) => r.given).length} given)`}
                  {" — categories: "}
                  {(safeSpec.groups || []).map((g) => `${g.label} × ${g.count}`).join(", ") || "add rows with a label + value"}
                </Typography>
              )}
              <FormControlLabel
                control={<Checkbox checked={!!safeSpec.require_labels} disabled={!!safeSpec.label_only || colourByGroup} onChange={(e) => update({ require_labels: e.target.checked })} />}
                label="Require the student to type each sector's label"
              />
              <Tooltip title="Use when the background picture already shows the FULL correct division/shading (e.g. every wedge is pre-drawn) — there is nothing to tap. Every sector is marked 'given' (pre-shaded, locked) and the student's only task is to type each sector's caption; shading is never part of the grade.">
                <FormControlLabel
                  control={<Checkbox checked={!!safeSpec.label_only} disabled={colourByGroup} onChange={(e) => update({ label_only: e.target.checked, require_labels: e.target.checked ? true : safeSpec.require_labels })} />}
                  label="Label only — picture already shows the shading, don't grade taps"
                />
              </Tooltip>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Marking</InputLabel>
              <Select label="Marking" value={safeSpec.target_mode || "exact"} onChange={(e) => update({ target_mode: e.target.value })}>
                <MenuItem value="exact">Exact cells (tap them)</MenuItem>
                <MenuItem value="count">Any N cells</MenuItem>
                <MenuItem value="sets">Any one of several arrangements</MenuItem>
                <MenuItem value="polyomino">Fixed pattern, drawn anywhere</MenuItem>
                <MenuItem value="metrics">Any shape by area / perimeter</MenuItem>
              </Select>
            </FormControl>
            {safeSpec.target_mode === "count" && (
              <TextField size="small" label="N cells" type="number" value={safeSpec.target_count || 0} onChange={(e) => update({ target_count: +e.target.value })} sx={{ width: 100 }} />
            )}
            {safeSpec.target_mode === "metrics" && (
              <>
                <TextField size="small" label="Area (cells)" type="number" placeholder="any" value={safeSpec.target_area ?? ""} onChange={(e) => update({ target_area: e.target.value === "" ? undefined : +e.target.value })} sx={{ width: 110 }} />
                <TextField size="small" label="Perimeter" type="number" placeholder="any" value={safeSpec.target_perimeter ?? ""} onChange={(e) => update({ target_perimeter: e.target.value === "" ? undefined : +e.target.value })} sx={{ width: 110 }} />
              </>
            )}
            <Typography variant="caption" color="text.secondary">
              {safeSpec.target_mode === "count"
                ? `Any ${safeSpec.target_count || 0} of ${(safeSpec.regions || []).length} regions`
                : safeSpec.target_mode === "metrics"
                ? `Any connected shape with ${safeSpec.target_area != null ? `area ${safeSpec.target_area}` : "any area"} and ${safeSpec.target_perimeter != null ? `perimeter ${safeSpec.target_perimeter}` : "any perimeter"}`
                : safeSpec.target_mode === "polyomino"
                ? `Pattern of ${(safeSpec.target_shape || []).length} cells — the student shades this shape anywhere (matched up to translation/rotation/reflection)`
                : safeSpec.target_mode === "sets"
                ? `${(safeSpec.target_sets || []).length} accepted arrangement(s) — the student matches any one`
                : `${(safeSpec.target_regions || []).length} of ${(safeSpec.regions || []).length} regions selected`}
            </Typography>
          </Box>
          {safeSpec.target_mode === "sets" && (
            // "Shade any ONE valid arrangement" where several distinct cell sets are all correct (e.g. the
            // 3 ways to shade 2 opposite faces of a cube net). Each way is captured from the current
            // shading; the grader (InteractiveDiagramGrading::gradeInteractiveShade, target_sets branch)
            // marks the student correct if their shading equals any saved set.
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", border: "1px dashed #c7c7c7", borderRadius: 1, p: 1.25 }}>
              <Typography variant="caption" color="text.secondary">
                Tap the cells for ONE correct arrangement, then “Add as an accepted way”. Repeat for every
                alternative — the student is marked correct if their shading matches ANY saved arrangement.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!(safeSpec.target_regions || []).length}
                  onClick={() => {
                    const cur = (safeSpec.target_regions || []).map((v) => (Number.isNaN(Number(v)) ? v : Number(v)));
                    if (!cur.length) return;
                    const key = (s) => [...s].map(String).sort().join(",");
                    const exists = (safeSpec.target_sets || []).some((s) => key(s) === key(cur));
                    update({
                      target_sets: exists ? (safeSpec.target_sets || []) : [...(safeSpec.target_sets || []), cur],
                      target_regions: [],
                    });
                  }}
                >
                  Add as an accepted way
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {(safeSpec.target_sets || []).length} way{(safeSpec.target_sets || []).length === 1 ? "" : "s"} saved
                  {(safeSpec.target_regions || []).length
                    ? ` · ${(safeSpec.target_regions || []).length} cell(s) shaded — click to add them as a way`
                    : " · shade the cells for a new arrangement on the diagram below, then this button turns on"}
                </Typography>
              </Box>
              {(safeSpec.target_sets || []).map((set, i) => {
                const desc = (set || [])
                  .map((id) => {
                    const rg = (safeSpec.regions || []).find((r) => String(r.id) === String(id));
                    return rg && Array.isArray(rg.cell) ? `(${rg.cell[0]},${rg.cell[1]})` : `#${id}`;
                  })
                  .join(" + ");
                return (
                  <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 52 }}>Way {i + 1}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{desc || "(empty)"}</Typography>
                    <Button size="small" onClick={() => update({ target_regions: (set || []).map((v) => (Number.isNaN(Number(v)) ? v : Number(v))) })}>Show</Button>
                    <Button size="small" color="error" onClick={() => update({ target_sets: (safeSpec.target_sets || []).filter((_, j) => j !== i) })}>Remove</Button>
                  </Box>
                );
              })}
            </Box>
          )}
          {(safeSpec.regions || []).length > 0 && (
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
              <Tooltip title="Cells already coloured in the picture. Turn this on, then tap those cells — they show grey, can't be tapped by the student, and are excluded from the count (e.g. 'shade 3/4' with 2 already shaded → set count to the remaining 7).">
                <FormControlLabel
                  control={<Checkbox size="small" checked={givenMode} onChange={(e) => setGivenMode(e.target.checked)} />}
                  label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Mark pre-coloured (given) cells</Typography>}
                />
              </Tooltip>
              <Typography variant="caption" color="text.secondary">
                {givenMode ? "Tap cells to flag/unflag them as given." : ""} {(safeSpec.regions || []).filter((r) => r.given).length} given
              </Typography>
            </Box>
          )}
          {(safeSpec.regions || []).length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Tooltip title="A cell that takes a TYPED LETTER instead of shading — 'write C in this section'. The student taps the shape to open a forced popup and type the letter; graded separately from any shaded regions. Colour/size (shown to the student while answering) let one cell stand out when several targets share a figure.">
                <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>Letter-entry cells (typed, not shaded)</Typography>
              </Tooltip>
              {(safeSpec.letter_regions || []).map((lr, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Region</InputLabel>
                    <Select
                      label="Region"
                      value={String(lr.id ?? "")}
                      onChange={(e) => {
                        const id = Number.isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value);
                        update({ letter_regions: (safeSpec.letter_regions || []).map((r, j) => (j === i ? { ...r, id } : r)) });
                      }}
                    >
                      {(safeSpec.regions || []).map((rg) => (
                        <MenuItem key={rg.id} value={String(rg.id)}>#{rg.id}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small" label="Char" value={lr.char || ""} sx={{ width: 70 }}
                    onChange={(e) => update({ letter_regions: (safeSpec.letter_regions || []).map((r, j) => (j === i ? { ...r, char: e.target.value.toUpperCase().slice(0, 2) } : r)) })}
                  />
                  <TextField
                    size="small" label="Color" type="color" value={lr.color || "#1976d2"} sx={{ width: 60 }}
                    onChange={(e) => update({ letter_regions: (safeSpec.letter_regions || []).map((r, j) => (j === i ? { ...r, color: e.target.value } : r)) })}
                  />
                  <TextField
                    size="small" label="Size (px)" type="number" value={lr.size || 92} sx={{ width: 100 }} inputProps={{ step: 4, min: 40 }}
                    onChange={(e) => update({ letter_regions: (safeSpec.letter_regions || []).map((r, j) => (j === i ? { ...r, size: +e.target.value || undefined } : r)) })}
                  />
                  <Button size="small" color="error" onClick={() => update({ letter_regions: (safeSpec.letter_regions || []).filter((_, j) => j !== i) })}>Remove</Button>
                </Box>
              ))}
              <Button
                size="small" variant="outlined" sx={{ alignSelf: "flex-start" }}
                onClick={() => update({ letter_regions: [...(safeSpec.letter_regions || []), { id: (safeSpec.regions || [])[0]?.id ?? 0, char: "" }] })}
              >
                Add letter cell
              </Button>
            </Box>
          )}
        </>
      )}

      {kind === "rect" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Define the snap grid, then set the required area. Any rectangle the student draws with this area is marked correct.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField size="small" label="x0" type="number" value={grid.x0} onChange={(e) => setGrid({ x0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="y0" type="number" value={grid.y0} onChange={(e) => setGrid({ y0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="dx" type="number" value={grid.dx} onChange={(e) => setGrid({ dx: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="dy" type="number" value={grid.dy} onChange={(e) => setGrid({ dy: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="cols" type="number" value={grid.cols} onChange={(e) => setGrid({ cols: e.target.value })} sx={{ width: 80 }} />
            <TextField size="small" label="rows" type="number" value={grid.rows} onChange={(e) => setGrid({ rows: e.target.value })} sx={{ width: 80 }} />
          </Box>
          <TextField size="small" label="Required area (grid squares)" type="number" value={safeSpec.target_area || 0} onChange={(e) => update({ target_area: +e.target.value })} sx={{ width: 220 }} inputProps={{ step: 1, min: 1 }} helperText="Tap two corners below to preview a sample rectangle" />
        </>
      )}

      {kind === "cut-drag" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            Tap two points to draw the model cut. Only its direction is graded — the student may cut anywhere across the shape, then drags the pieces together. The shape corners come from the imported spec (spec.shape).
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" label="Angle tol (°)" type="number" value={safeSpec.angle_tol_deg ?? 8} onChange={(e) => update({ angle_tol_deg: +e.target.value })} sx={{ width: 110 }} inputProps={{ step: 1, min: 1 }} />
            <TextField size="small" label="Offset tol" type="number" value={safeSpec.offset_tol ?? 0.07} onChange={(e) => update({ offset_tol: +e.target.value })} sx={{ width: 110 }} inputProps={{ step: 0.01, min: 0.01 }} />
            <Typography variant="caption" color="text.secondary">
              {Array.isArray(safeSpec.target_cut) ? "Model cut defined" : "No cut yet"} · {(safeSpec.shape || []).length} shape corner(s)
            </Typography>
          </Box>
        </>
      )}
      {kind === "lines" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Tap two points on the diagram to draw each correct line (line of symmetry / mirror line). Add as many as the answer needs; tap a line to remove it. Or tick "No lines of symmetry" if the answer is that there are none.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={noSymmetry} onChange={(e) => update(e.target.checked ? { target_lines: [], target_count: 0 } : { target_count: (safeSpec.target_lines || []).length })} />}
              label={<Typography variant="caption">No lines of symmetry (empty answer is correct)</Typography>}
            />
            <TextField size="small" label="Angle tol (°)" type="number" value={safeSpec.angle_tol_deg ?? 6} onChange={(e) => update({ angle_tol_deg: +e.target.value })} sx={{ width: 110 }} inputProps={{ step: 1, min: 1 }} />
            <TextField size="small" label="Distance tol" type="number" value={safeSpec.dist_tol ?? 0.04} onChange={(e) => update({ dist_tol: +e.target.value })} sx={{ width: 120 }} inputProps={{ step: 0.01, min: 0.01 }} />
            <Typography variant="caption" color="text.secondary">
              {noSymmetry ? "Answer: no lines" : `${(safeSpec.target_lines || []).length} line(s) defined`}
            </Typography>
          </Box>
        </>
      )}

      {kind === "segments" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Enter the node (cross) positions as fractional [x, y] coordinates, one pair per row. 2) Tap one cross then another on the diagram to mark each correct join; tap a line to remove it.
          </Typography>
          <TextField
            size="small" fullWidth multiline minRows={3}
            label="Nodes — JSON array of [x, y] (0..1)"
            value={JSON.stringify(safeSpec.nodes || [])}
            onChange={(e) => { try { const n = JSON.parse(e.target.value); if (Array.isArray(n)) update({ nodes: n, target_segments: [] }); } catch { /* keep typing */ } }}
            inputProps={{ style: { fontFamily: "monospace", fontSize: "0.75rem" } }}
          />
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" label="Snap tol" type="number" value={safeSpec.snap_tol ?? 0.05} onChange={(e) => update({ snap_tol: +e.target.value })} sx={{ width: 120 }} inputProps={{ step: 0.01, min: 0.01 }} />
            <Typography variant="caption" color="text.secondary">
              {(safeSpec.nodes || []).length} node(s), {(safeSpec.target_segments || []).length} join(s) defined
            </Typography>
          </Box>
        </>
      )}

      {kind === "polygon" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Define the snap grid, choose the required shape + area, then tap the vertices of one correct example. Any shape of that type with the same area (incl. tilted) is marked correct.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField size="small" label="x0" type="number" value={grid.x0} onChange={(e) => setGrid({ x0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="y0" type="number" value={grid.y0} onChange={(e) => setGrid({ y0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="dx" type="number" value={grid.dx} onChange={(e) => setGrid({ dx: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="dy" type="number" value={grid.dy} onChange={(e) => setGrid({ dy: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="cols" type="number" value={grid.cols} onChange={(e) => setGrid({ cols: e.target.value })} sx={{ width: 80 }} />
            <TextField size="small" label="rows" type="number" value={grid.rows} onChange={(e) => setGrid({ rows: e.target.value })} sx={{ width: 80 }} />
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Required shape</InputLabel>
              <Select label="Required shape" value={safeSpec.require_shape || "any"} onChange={(e) => update({ require_shape: e.target.value === "any" ? null : e.target.value })}>
                <MenuItem value="any">Any polygon</MenuItem>
                <MenuItem value="triangle">Triangle</MenuItem>
                <MenuItem value="square">Square</MenuItem>
                <MenuItem value="rectangle">Rectangle</MenuItem>
                <MenuItem value="parallelogram">Parallelogram</MenuItem>
                <MenuItem value="rhombus">Rhombus</MenuItem>
                <MenuItem value="kite">Kite</MenuItem>
              </Select>
            </FormControl>
            {/* When no named shape fits (e.g. "draw a hexagon of area 12"), constrain the vertex
                count instead — the polygon grader accepts any simple N-gon of that area. */}
            <TextField size="small" label="Vertices (N-gon)" type="number" value={safeSpec.require_vertices || ""} onChange={(e) => update({ require_vertices: e.target.value === "" ? null : +e.target.value })} sx={{ width: 140 }} inputProps={{ step: 1, min: 3 }} helperText="blank = any" />
            <TextField size="small" label="Area (grid squares)" type="number" value={safeSpec.target_area || 0} onChange={(e) => update({ target_area: +e.target.value })} sx={{ width: 180 }} inputProps={{ step: 0.5, min: 0.5 }} disabled={multiRect} helperText={multiRect ? "using Areas below" : undefined} />
            <TextField size="small" label="Area tol" type="number" value={safeSpec.area_tol ?? 0.05} onChange={(e) => update({ area_tol: +e.target.value })} sx={{ width: 100 }} inputProps={{ step: 0.05, min: 0 }} />
          </Box>
          {/* "Sketch the next N rectangles" — grade several separate closed shapes on one grid by their
              set of areas. Corners are grouped in tap order (4 per rectangle), each graded by area. */}
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={multiRect} onChange={(e) => update(e.target.checked
                ? { require_shape: "rectangle", target_areas: [safeSpec.target_area || 1], target_area: undefined }
                : { target_areas: undefined, point_count: undefined, shape_count: undefined })} />}
              label={<Typography variant="caption">Several rectangles on one grid (by area)</Typography>}
            />
            {multiRect && (
              <TextField
                size="small" label="Areas (comma-separated grid squares)" sx={{ width: 300 }}
                value={(safeSpec.target_areas || []).join(", ")}
                onChange={(e) => {
                  const areas = e.target.value.split(",").map((s) => parseFloat(s.trim())).filter((n) => n > 0);
                  update({ target_areas: areas.length ? areas : [], shape_count: Math.max(1, areas.length), point_count: Math.max(4, areas.length * 4) });
                }}
                helperText={`${(safeSpec.target_areas || []).length} rectangle(s) — student taps 4 corners each, in order`}
              />
            )}
          </Box>
          {/* "AC is the longer diagonal of kite ABCD" — A and C are printed on the paper, so the student
              joins to them but never plots them. They stay corners of the graded shape (given + placed),
              and the student's tap budget drops to the corners they actually have to add. */}
          {!multiRect && (
            <>
              <TextField
                size="small"
                fullWidth
                label="Corners already printed on the diagram (e.g. “3,8 A; 7,8 C”)"
                value={(safeSpec.given_points || []).map((p) => `${p[0]},${p[1]}${p[2] ? ` ${p[2]}` : ""}`).join("; ")}
                onChange={(e) => {
                  const given = e.target.value.split(";").map((chunk) => {
                    const m = chunk.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)(?:\s+(.+))?$/);
                    return m ? [Number(m[1]), Number(m[2]), ...(m[3] ? [m[3].trim()] : [])] : null;
                  }).filter(Boolean);
                  const corners = (safeSpec.target_points || []).length;
                  update({
                    given_points: given.length ? given : undefined,
                    point_count: given.length && corners > given.length ? corners - given.length : undefined,
                  });
                }}
                helperText="Grid coords, optional letter. These are drawn grey, can be joined but not moved or re-plotted, and count towards the shape. Leave blank if the student draws every corner. Still tap ALL the shape's corners on the grid below — the printed ones included."
              />
              {/* "Complete the shape" — part of the outline is PRINTED on the image, so a full
                  student-drawn ring can't be demanded. required_edges names exactly the edges the
                  student must draw (either end may be a printed corner); the backend grades only
                  these and skips the closed-ring check. */}
              {!!(safeSpec.given_points || []).length && (
                <TextField
                  size="small"
                  fullWidth
                  label="Edges the student must draw (e.g. “2,5-3,7; 3,7-2,8”)"
                  value={(safeSpec.required_edges || []).map((e2) => `${e2[0][0]},${e2[0][1]}-${e2[1][0]},${e2[1][1]}`).join("; ")}
                  onChange={(e) => {
                    const edges = e.target.value.split(";").map((chunk) => {
                      const m = chunk.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
                      return m ? [[Number(m[1]), Number(m[2])], [Number(m[3]), Number(m[4])]] : null;
                    }).filter(Boolean);
                    update({ required_edges: edges.length ? edges : undefined });
                  }}
                  helperText="Only for shapes whose outline is partly printed: list each line the student must add, as corner-corner pairs (grid coords, printed or plotted). Leave blank to grade the full drawn outline instead."
                />
              )}
              <FormControlLabel
                control={<Checkbox size="small" checked={safeSpec.allow_labels !== false} onChange={(e) => update({ allow_labels: e.target.checked })} />}
                label={<Typography variant="caption">Label the corners (student types a letter on each corner, e.g. “B”)</Typography>}
              />
              {safeSpec.allow_labels !== false && pointLabelsBlock}
              <FormControlLabel
                control={<Checkbox size="small" checked={!!safeSpec.require_shape_label} onChange={(e) => update({ require_shape_label: e.target.checked, shape_label: e.target.checked ? (safeSpec.shape_label || "A") : (safeSpec.shape_label || undefined) })} />}
                label={<Typography variant="caption">Require a shape label (e.g. “Label the kite A” — popup after the shape is drawn)</Typography>}
              />
              {!!safeSpec.require_shape_label && (
                <TextField
                  size="small"
                  label="Expected shape label"
                  value={safeSpec.shape_label || ""}
                  onChange={(e) => update({ shape_label: e.target.value.trim() })}
                  sx={{ width: 160 }}
                />
              )}
            </>
          )}
        </>
      )}

      {kind === "glyph" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Tap the diagram to add each correct symbol. Type it, or — with a symbol palette — tap it from the palette below the canvas. Grading matches symbol + position{safeSpec.match_orientation ? " + orientation" : ""}: for a reflect/rotate tile, use the glyph that already points the right way (▽ for a triangle after a half turn) rather than relying on rotation.
          </Typography>
          {/* Answer targets are the graded cells; optional targets are a "you may complete this if you
              wish" working grid (e.g. the middle tile) — never graded, never an extra. Switch which set
              the canvas edits; both are saved. */}
          <ToggleButtonGroup size="small" exclusive value={glyphLayer} onChange={(_, v) => { if (v) setGlyphLayer(v); }}>
            <ToggleButton value="answer" sx={{ textTransform: "none", py: 0.25 }}>Answer targets ({(safeSpec.targets || []).length})</ToggleButton>
            <ToggleButton value="optional" sx={{ textTransform: "none", py: 0.25 }}>Working / optional ({(safeSpec.optional_targets || []).length})</ToggleButton>
          </ToggleButtonGroup>
          {glyphLayer === "optional" && (
            <Typography variant="caption" color="text.secondary">
              Optional cells are never graded — a student filling them (right or wrong) is never penalised, and leaving them empty costs nothing.
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <FormControlLabel control={<Checkbox size="small" checked={safeSpec.use_grid !== false} onChange={(e) => update({ use_grid: e.target.checked })} />} label={<Typography variant="caption">Snap to grid cells</Typography>} />
            <FormControlLabel control={<Checkbox size="small" checked={!!safeSpec.match_orientation} onChange={(e) => update({ match_orientation: e.target.checked })} />} label={<Typography variant="caption">Require matching orientation (flip/rotate)</Typography>} />
            <TextField size="small" label="Position tol" type="number" value={safeSpec.pos_tol ?? (safeSpec.use_grid !== false ? 0.03 : 0.06)} onChange={(e) => update({ pos_tol: +e.target.value })} sx={{ width: 110 }} inputProps={{ step: 0.01, min: 0.01 }} />
            {/* Aspect (width/height) of the figure — widens the vertical position tolerance so a fraction
                means the same visual distance on a wide, short image (e.g. a 3-grid strip, ~3.15). */}
            <TextField size="small" label="Image aspect" type="number" value={safeSpec.image_aspect ?? ""} placeholder="auto"
              onChange={(e) => { const v = parseFloat(e.target.value); update({ image_aspect: Number.isFinite(v) && v > 0 ? v : undefined }); }}
              sx={{ width: 120 }} inputProps={{ step: 0.05, min: 0.1 }} helperText="w/h; blank = auto" />
            {/* Optional fixed symbol set (comma-separated) — the student taps these instead of typing,
                for non-keyboard glyphs like + ○ △ ↔ ▽ (reflect/rotate-a-tile questions). */}
            <TextField size="small" label="Symbol palette" value={(safeSpec.symbol_palette || []).join(",")} placeholder="e.g. +,○,△,↔,▽"
              onChange={(e) => { const arr = e.target.value.split(",").map((s) => s.trim()).filter(Boolean); update({ symbol_palette: arr.length ? arr : null }); }}
              sx={{ width: 190 }} helperText="blank = free text" />
            <Typography variant="caption" color="text.secondary">{((glyphLayer === "optional" ? safeSpec.optional_targets : safeSpec.targets) || []).length} symbol(s) set</Typography>
          </Box>
          {safeSpec.use_grid !== false && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <TextField size="small" label="x0" type="number" value={grid.x0} onChange={(e) => setGrid({ x0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="y0" type="number" value={grid.y0} onChange={(e) => setGrid({ y0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="dx" type="number" value={grid.dx} onChange={(e) => setGrid({ dx: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="dy" type="number" value={grid.dy} onChange={(e) => setGrid({ dy: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="cols" type="number" value={grid.cols} onChange={(e) => setGrid({ cols: e.target.value })} sx={{ width: 80 }} />
              <TextField size="small" label="rows" type="number" value={grid.rows} onChange={(e) => setGrid({ rows: e.target.value })} sx={{ width: 80 }} />
            </Box>
          )}
        </>
      )}

      {(kind === "reflect" || kind === "rotate") && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Mark the <b>original shape</b>'s corners on the grid, then set the {kind === "reflect" ? "mirror line" : "rotation centre + angle"}. The correct {kind === "reflect" ? "reflection" : "rotated shape"} is computed automatically — students place its corners on the grid.
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField size="small" label="x0" type="number" value={grid.x0} onChange={(e) => setGrid({ x0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="y0" type="number" value={grid.y0} onChange={(e) => setGrid({ y0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="dx" type="number" value={grid.dx} onChange={(e) => setGrid({ dx: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="dy" type="number" value={grid.dy} onChange={(e) => setGrid({ dy: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="cols" type="number" value={grid.cols} onChange={(e) => setGrid({ cols: e.target.value })} sx={{ width: 80 }} />
            <TextField size="small" label="rows" type="number" value={grid.rows} onChange={(e) => setGrid({ rows: e.target.value })} sx={{ width: 80 }} />
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <ToggleButtonGroup size="small" exclusive value={transformMode} onChange={(_, v) => { if (v) setTransformMode(v); }}>
              <ToggleButton value="shape" sx={{ textTransform: "none", fontSize: "0.72rem" }}>Original shape</ToggleButton>
              <ToggleButton value="axis" sx={{ textTransform: "none", fontSize: "0.72rem" }}>{kind === "reflect" ? "Mirror line" : "Rotation centre"}</ToggleButton>
              <ToggleButton value="preview" sx={{ textTransform: "none", fontSize: "0.72rem" }}>Preview target</ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel control={<Checkbox size="small" checked={safeSpec.closed !== false} onChange={(e) => update({ closed: e.target.checked })} />} label={<Typography variant="caption">Closed shape</Typography>} />
            <FormControlLabel control={<Checkbox size="small" checked={safeSpec.allow_labels === true} onChange={(e) => update({ allow_labels: e.target.checked })} />} label={<Typography variant="caption">Label corners (only for "reflect and label B")</Typography>} />
            {kind === "rotate" && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Angle</InputLabel>
                <Select label="Angle" value={safeSpec.angle || "cw90"} onChange={(e) => update({ angle: e.target.value, target_points: computeRotateTarget(safeSpec.original_shape || [], safeSpec.center, e.target.value) })}>
                  <MenuItem value="cw90">90° clockwise</MenuItem>
                  <MenuItem value="ccw90">90° anti-clockwise</MenuItem>
                  <MenuItem value="180">180°</MenuItem>
                </Select>
              </FormControl>
            )}
            <Typography variant="caption" color="text.secondary">
              {(safeSpec.original_shape || []).length} corner(s) · target {(safeSpec.target_points || []).length} pt(s)
            </Typography>
          </Box>
        </>
      )}

      {kind === "pictogram" && (() => {
        const row = safeSpec.row || { x0: 0.4, y: 0.85, dx: 0.06, count: 6 };
        const setRow = (patch) => update({ row: { ...row, ...patch } });
        return (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
              1) Position the row of icon slots over the empty pictogram row, then tap the icons below to build the correct answer (tap = whole → ¾ → ½ → ¼ → empty). Students complete the row the same way; grading compares the total.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <TextField size="small" label="x0 (first icon)" type="number" value={row.x0} onChange={(e) => setRow({ x0: +e.target.value })} sx={{ width: 120 }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="y (row)" type="number" value={row.y} onChange={(e) => setRow({ y: +e.target.value })} sx={{ width: 100 }} inputProps={{ step: 0.01 }} />
              <TextField size="small" label="dx (spacing)" type="number" value={row.dx} onChange={(e) => setRow({ dx: +e.target.value })} sx={{ width: 110 }} inputProps={{ step: 0.005 }} />
              <TextField size="small" label="slots" type="number" value={row.count} onChange={(e) => setRow({ count: Math.max(0, Math.round(+e.target.value)) })} sx={{ width: 90 }} inputProps={{ min: 1 }} />
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Icon</InputLabel>
                <Select label="Icon" value={safeSpec.icon || "bottle"} onChange={(e) => update({ icon: e.target.value })}>
                  <MenuItem value="bottle">Bottle</MenuItem>
                  <MenuItem value="lorry">Lorry</MenuItem>
                  <MenuItem value="circle">Circle</MenuItem>
                  <MenuItem value="star">Star</MenuItem>
                  <MenuItem value="square">Square</MenuItem>
                  <MenuItem value="cross">Cross / plus</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
              <TextField size="small" label="Each icon =" type="number" value={safeSpec.per_icon ?? ""} onChange={(e) => update({ per_icon: e.target.value === "" ? null : +e.target.value })} sx={{ width: 120 }} helperText="key value (optional)" />
              <TextField size="small" label="Target (icons)" type="number" value={safeSpec.target_icons ?? 0} onChange={(e) => update({ target_icons: +e.target.value })} sx={{ width: 130 }} inputProps={{ step: 0.25, min: 0 }} />
              <TextField size="small" label="Tolerance" type="number" value={safeSpec.tol ?? 0} onChange={(e) => update({ tol: +e.target.value })} sx={{ width: 110 }} helperText="0 = exact" inputProps={{ step: 0.25, min: 0 }} />
              <Typography variant="caption" color="text.secondary">
                {(safeSpec.target_icons ?? 0)} icon(s){safeSpec.per_icon ? ` = ${(safeSpec.target_icons ?? 0) * safeSpec.per_icon}` : ""}
              </Typography>
            </Box>
          </>
        );
      })()}

      {kind === "clock" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Set the target time. The clock face comes from the question image; students drag the hour &amp; minute hands to show it.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" label="Hour (0–23)" type="number" value={safeSpec.target_hour ?? 3} onChange={(e) => update({ target_hour: +e.target.value })} sx={{ width: 110 }} inputProps={{ min: 0, max: 23 }} />
            <TextField size="small" label="Minute (0–59)" type="number" value={safeSpec.target_minute ?? 0} onChange={(e) => update({ target_minute: +e.target.value })} sx={{ width: 120 }} inputProps={{ min: 0, max: 59 }} />
            <TextField size="small" label="Centre x" type="number" value={clockCenter[0]} onChange={(e) => update({ center: [+e.target.value, clockCenter[1]] })} sx={{ width: 90 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="Centre y" type="number" value={clockCenter[1]} onChange={(e) => update({ center: [clockCenter[0], +e.target.value] })} sx={{ width: 90 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="Radius" type="number" value={clockRadius} onChange={(e) => update({ radius: +e.target.value })} sx={{ width: 90 }} inputProps={{ step: 0.02, min: 0.1 }} />
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" label="Hour tol (°)" type="number" value={safeSpec.hour_tol_deg ?? 12} onChange={(e) => update({ hour_tol_deg: +e.target.value })} sx={{ width: 120 }} inputProps={{ step: 1, min: 1 }} />
            <TextField size="small" label="Minute tol (°)" type="number" value={safeSpec.minute_tol_deg ?? 8} onChange={(e) => update({ minute_tol_deg: +e.target.value })} sx={{ width: 130 }} inputProps={{ step: 1, min: 1 }} />
            <Typography variant="caption" color="text.secondary">Drag the hands below to fine-tune (hour snaps by position, minute to nearest minute).</Typography>
          </Box>
        </>
      )}

      {kind === "marker" && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Draw the <b>axis</b> (tap the two ends of the number line / scale / bar-chart value axis), give the value at each end, then switch to <b>Target</b> and tap the correct value. Students tap once to mark it.
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <ToggleButtonGroup size="small" exclusive value={markerMode} onChange={(_, v) => { if (v) setMarkerMode(v); }}>
              <ToggleButton value="axis" sx={{ textTransform: "none", fontSize: "0.72rem" }}>Axis (tap 2 ends)</ToggleButton>
              <ToggleButton value="target" sx={{ textTransform: "none", fontSize: "0.72rem" }} disabled={!safeSpec.axis}>Target value</ToggleButton>
            </ToggleButtonGroup>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Style</InputLabel>
              <Select label="Style" value={safeSpec.style || "arrow"} onChange={(e) => update({ style: e.target.value })}>
                <MenuItem value="arrow">Arrow / tick</MenuItem>
                <MenuItem value="bar">Bar (fill)</MenuItem>
              </Select>
            </FormControl>
            {(safeSpec.style === "bar") && (
              <TextField size="small" label="Bar width" type="number" value={safeSpec.bar_width ?? 0.06} onChange={(e) => update({ bar_width: +e.target.value })} sx={{ width: 100 }} inputProps={{ step: 0.01, min: 0.01 }} />
            )}
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" label="Value at start" type="number" value={safeSpec.v0 ?? 0} onChange={(e) => update({ v0: +e.target.value })} sx={{ width: 120 }} />
            <TextField size="small" label="Value at end" type="number" value={safeSpec.v1 ?? 10} onChange={(e) => update({ v1: +e.target.value })} sx={{ width: 120 }} />
            <TextField size="small" label="Target value" type="number" value={safeSpec.target_value ?? ""} onChange={(e) => {
              if (e.target.value === "") return update({ target_value: null, target_point: null });
              const tv = +e.target.value; const v0 = num(safeSpec.v0, 0), v1 = num(safeSpec.v1, 10);
              let tp = null;
              if (safeSpec.axis && v1 !== v0) { let t = (tv - v0) / (v1 - v0); t = Math.max(0, Math.min(1, t)); const a = safeSpec.axis[0], b = safeSpec.axis[1]; tp = [num(a[0]) + t * (num(b[0]) - num(a[0])), num(a[1]) + t * (num(b[1]) - num(a[1]))]; }
              update({ target_value: tv, target_point: tp });
            }} sx={{ width: 120 }} helperText="or tap on the axis" />
            <TextField size="small" label="Tolerance" type="number" value={safeSpec.tol ?? ""} onChange={(e) => update({ tol: e.target.value === "" ? null : +e.target.value })} sx={{ width: 110 }} helperText="value units (0 = 3% span)" inputProps={{ step: 0.1, min: 0 }} />
            {/* "Mark with letter A the likelihood that…" — the paper names the mark, so the canvas draws
                that letter above the point the student taps. Fixed by the question, never typed, so it
                adds nothing to grade. */}
            <TextField size="small" label="Marker label" value={safeSpec.marker_label ?? ""}
              onChange={(e) => update({ marker_label: e.target.value.trim() === "" ? null : e.target.value.trim() })}
              sx={{ width: 120 }} helperText='e.g. "A" — drawn on the mark' inputProps={{ maxLength: 4 }} />
          </Box>
          {safeSpec.axis && safeSpec.target_value != null && (
            <Alert severity="info" sx={{ mt: 0.5 }}>
              Students tap the scale once. Correct when the tapped value is within{" "}
              <strong>±{safeSpec.tol || (Math.abs(num(safeSpec.v1, 10) - num(safeSpec.v0, 0)) * 0.03).toFixed(3)}</strong>{" "}
              of <strong>{safeSpec.target_value}</strong>
              {safeSpec.marker_label ? <> — and the mark is drawn as “<strong>{safeSpec.marker_label}</strong>”.</> : "."}
            </Alert>
          )}
          {!safeSpec.axis && (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              No <strong>axis</strong> set. Without it the student has nothing to tap and the answer can
              never be graded — switch to “Axis” above and tap the two ends of the scale.
            </Alert>
          )}
        </>
      )}

      {(kind === "points" || kind === "drag-shape") && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
            1) Define the snap grid, then tap the correct {kind === "drag-shape" ? "placement / original shape" : "points"} below.
          </Typography>
          {kind === "points" && (
            <Button
              size="small"
              variant={Number(grid.rows) === 0 ? "contained" : "outlined"}
              onClick={() => update({ grid: { ...grid, rows: 0 }, connect: false, closed: false })}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Single horizontal dotted line
            </Button>
          )}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <TextField size="small" label="x0" type="number" value={grid.x0} onChange={(e) => setGrid({ x0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="y0" type="number" value={grid.y0} onChange={(e) => setGrid({ y0: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="dx" type="number" value={grid.dx} onChange={(e) => setGrid({ dx: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="dy" type="number" value={grid.dy} onChange={(e) => setGrid({ dy: e.target.value })} sx={{ width: 80 }} inputProps={{ step: 0.01 }} />
            <TextField size="small" label="cols" type="number" value={grid.cols} onChange={(e) => setGrid({ cols: e.target.value })} sx={{ width: 80 }} inputProps={{ min: 0 }} />
            <TextField size="small" label="rows" type="number" value={grid.rows} onChange={(e) => setGrid({ rows: e.target.value })} sx={{ width: 80 }} inputProps={{ min: 0 }} helperText={kind === "points" ? "0 = one line" : undefined} />
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" label="Vertices" type="number" value={safeSpec.point_count || 0} onChange={(e) => update({ point_count: +e.target.value })} sx={{ width: 110 }} helperText="0 = any" />
            <FormControlLabel control={<Checkbox size="small" checked={safeSpec.connect !== false} onChange={(e) => update({ connect: e.target.checked })} />} label={<Typography variant="caption">Connect points</Typography>} />
            {safeSpec.connect !== false && (
              <FormControlLabel control={<Checkbox size="small" checked={safeSpec.closed !== false} onChange={(e) => update({ closed: e.target.checked })} />} label={<Typography variant="caption">Close shape</Typography>} />
            )}
            {safeSpec.connect !== false && (
              <TextField size="small" label="Separate lines" type="number" value={safeSpec.shape_count || 1} onChange={(e) => update({ shape_count: Math.max(1, +e.target.value || 1) })} sx={{ width: 130 }} helperText="1 = join all points" />
            )}
            {kind === "drag-shape" && (
              <ToggleButtonGroup size="small" exclusive value={editTarget} onChange={(_, v) => { if (v) setEditTarget(v); }}>
                <ToggleButton value="original" sx={{ textTransform: "none", fontSize: "0.72rem" }}>Original shape</ToggleButton>
                <ToggleButton value="answer" sx={{ textTransform: "none", fontSize: "0.72rem" }}>Correct placement</ToggleButton>
              </ToggleButtonGroup>
            )}
            <FormControlLabel control={<Checkbox size="small" checked={safeSpec.allow_labels !== false} onChange={(e) => {
              const on = e.target.checked;
              update(on ? { allow_labels: true } : { allow_labels: false, require_point_labels: false });
            }} />} label={<Typography variant="caption">Point labels</Typography>} />
          </Box>
          {safeSpec.allow_labels !== false && pointLabelsBlock}
          {/* "Join K to L" where K is already printed/plotted on the diagram: the student only ever taps
              L (below), never K, so a join to it can't be graded by tap order (there's no tap to order) —
              it's drawn as an explicit edge instead (tap K's dot, then L's). Only meaningful on an OPEN
              plot (Close shape off): a closed shape already gets given-corner joining for free via its
              derived outline, with no need to name edges by hand. */}
          {/* Printed corners live under given_points on the authored specs and context_points on ones
              authored here; the canvas and the grader treat them identically, so edit whichever key the
              spec already uses. Shown for closed shapes too — the kite whose three corners are printed
              needs exactly these two fields, and gating them on an open shape hid them completely. */}
          {kind === "points" && safeSpec.connect !== false && (
            <>
              <TextField
                size="small"
                fullWidth
                label="Point(s) already given on the diagram (e.g. “6,9 K”)"
                value={(safeSpec.given_points || safeSpec.context_points || []).map((p) => `${p[0]},${p[1]}${p[2] ? ` ${p[2]}` : ""}`).join("; ")}
                onChange={(e) => {
                  const given = e.target.value.split(";").map((chunk) => {
                    const m = chunk.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)(?:\s+(.+))?$/);
                    return m ? [Number(m[1]), Number(m[2]), ...(m[3] ? [m[3].trim()] : [])] : null;
                  }).filter(Boolean);
                  const key = (safeSpec.given_points || []).length > 0 ? 'given_points' : 'context_points';
                  update({
                    [key]: given.length ? given : undefined,
                    // Seed an empty (but present) array so the canvas's edge-drawing mode is available the
                    // moment a given point exists — it gates on `required_edges` being an array, and with
                    // no edges drawn yet that would otherwise stay `undefined` and never turn the mode on.
                    required_edges: given.length ? (Array.isArray(safeSpec.required_edges) ? safeSpec.required_edges : []) : undefined,
                  });
                }}
                helperText="Grid coords, optional letter. Drawn grey, never tapped/plotted/labelled by the student — only joinable (see Required joins below). Leave blank if every point is plotted by the student."
              />
              {((safeSpec.given_points || []).length > 0 || (safeSpec.context_points || []).length > 0) && (
                <TextField
                  size="small"
                  fullWidth
                  label="Required joins (e.g. “6,9-6,5; 4,3-0,3”)"
                  value={(safeSpec.required_edges || []).map(([a, b]) => `${a[0]},${a[1]}-${b[0]},${b[1]}`).join("; ")}
                  onChange={(e) => {
                    const edges = e.target.value.split(";").map((chunk) => {
                      const m = chunk.trim().match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
                      return m ? [[Number(m[1]), Number(m[2])], [Number(m[3]), Number(m[4])]] : null;
                    }).filter(Boolean);
                    update({ required_edges: edges.length ? edges : undefined });
                  }}
                  helperText="Grid-coordinate pairs (from-x,y-to-x,y) that must be joined with a straight line — either end may be one of the given point(s) above, or one of the plotted points below. The student draws each by tapping one dot then the other."
                />
              )}
              {((safeSpec.given_points || []).length > 0 || (safeSpec.context_points || []).length > 0) && (
                <FormControlLabel
                  control={<Checkbox size="small" checked={!!safeSpec.no_new_points} onChange={(e) => update({ no_new_points: e.target.checked || undefined })} />}
                  label={<Typography variant="caption">Joining only — no new points (every corner is already on the diagram; an empty-cell tap plants nothing)</Typography>}
                />
              )}
            </>
          )}
          <FormControlLabel
            control={<Checkbox size="small" checked={!!safeSpec.require_shape_label} onChange={(e) => update({ require_shape_label: e.target.checked, shape_label: e.target.checked ? (safeSpec.shape_label || "A") : (safeSpec.shape_label || undefined) })} />}
            label={<Typography variant="caption">Require a shape label (e.g. “Label the triangle A” — popup after the shape is drawn)</Typography>}
          />
          {!!safeSpec.require_shape_label && (
            <TextField
              size="small"
              label="Expected shape label"
              value={safeSpec.shape_label || ""}
              onChange={(e) => update({ shape_label: e.target.value.trim() })}
              sx={{ width: 160 }}
              helperText="Letter shown in the centre of the finished shape (A, B, C…)"
            />
          )}
        </>
      )}

      {(kind === "reflect" || kind === "rotate") && (
        <>
          <FormControlLabel
            control={<Checkbox size="small" checked={safeSpec.allow_labels === true} onChange={(e) => update({ allow_labels: e.target.checked })} />}
            label={<Typography variant="caption">Let the student label corners of the {kind === "reflect" ? "reflected" : "rotated"} shape (type a letter on a corner, e.g. “B”)</Typography>}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={!!safeSpec.require_shape_label} onChange={(e) => update({ require_shape_label: e.target.checked, shape_label: e.target.checked ? (safeSpec.shape_label || "A") : (safeSpec.shape_label || undefined) })} />}
            label={<Typography variant="caption">Require a shape label (e.g. “Label the image C” — popup after the shape is drawn)</Typography>}
          />
          {!!safeSpec.require_shape_label && (
            <TextField
              size="small"
              label="Expected shape label"
              value={safeSpec.shape_label || ""}
              onChange={(e) => update({ shape_label: e.target.value.trim() })}
              sx={{ width: 160 }}
            />
          )}
        </>
      )}

      <Box sx={{ border: "1px dashed #cfcfcf", borderRadius: 1, p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={!!safeSpec.written} onChange={(e) => update({ written: e.target.checked ? { prompt: safeSpec.written?.prompt || "", answer: safeSpec.written?.answer || "" } : null })} />}
          label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Also ask for a written answer (e.g. “write down the co-ordinates”)</Typography>}
        />
        {safeSpec.written && (
          <>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TextField size="small" label="Prompt shown to student" value={safeSpec.written.prompt || ""} onChange={(e) => update({ written: { ...safeSpec.written, prompt: e.target.value } })} sx={{ minWidth: 240, flex: 1 }} />
              <TextField size="small" label="Expected answer (e.g. (7, 4))" value={safeSpec.written.answer || ""} onChange={(e) => update({ written: { ...safeSpec.written, answer: e.target.value } })} sx={{ minWidth: 180 }} />
            </Box>
            <FormControlLabel
              control={<Checkbox size="small" checked={safeSpec.written.require_drawing !== false} onChange={(e) => update({ written: { ...safeSpec.written, require_drawing: e.target.checked } })} />}
              label={<Typography variant="caption">Drawing also required (uncheck when the written answer alone earns the mark, e.g. “write down the co-ordinates”)</Typography>}
            />
            <Typography variant="caption" color="text.secondary">
              The student sees a text box under the diagram. Coordinate spacing/parentheses are ignored when matching.
            </Typography>
          </>
        )}
      </Box>

      {bgImage && canMoveGrid && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={moveMode} onChange={(e) => setMoveMode(e.target.checked)} />}
            label={<Typography variant="caption" sx={{ fontWeight: 700 }}>Move grid (drag it on the image)</Typography>}
          />
          <Typography variant="caption" color="text.secondary">
            {moveMode ? "Drag anywhere on the diagram to reposition the grid. Turn off to tap individual cells." : "Turn on to drag the whole grid into place instead of editing x/y."}
          </Typography>
        </Box>
      )}

      <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1, bgcolor: "#fafafa", alignSelf: "flex-start", maxWidth: "100%" }}>
        {bgImage ? (
          <InteractiveDiagramCanvas
            spec={canvasSpec}
            imageUrl={bgImage}
            value={canvasValue}
            onChange={canvasOnChange}
            contextLayers={contextLayers}
            gridMovable={moveMode && canMoveGrid}
            onMoveGrid={moveGrid}
            onToggleGiven={kind === "shade" && givenMode ? toggleGiven : null}
          />
        ) : (
          <Typography variant="caption" color="text.disabled">Attach the question image first, then configure the interactive answer.</Typography>
        )}
      </Box>
    </Box>
  );
};

export default InteractiveDiagramEditor;

// Mirrors the maths:unify-shared-grids artisan command. The take page keys its shared grid by image
// filename, so parts pointing at different crops of the same printed grid each get their own canvas.
// Lattice coords are crop-independent; only the fractional calibration is, so unifying is copying one
// part's image_url + grid onto its siblings — safe only when the calibrations describe the same extent.
const SPACING_TOL = 0.2;

const gridKey = (url) => {
  try {
    return (url || "").split("?")[0].split("/").pop() || "";
  } catch {
    return "";
  }
};

const gridParts = (subQuestions) =>
  (subQuestions || []).reduce((out, sq, index) => {
    const spec = sq?.interactiveSpec;
    if (spec?.image_url && spec?.grid) out.push({ index, spec, key: gridKey(spec.image_url) });
    return out;
  }, []);

const incompatibleReason = (parts, target, labelOf) => {
  for (const p of parts) {
    for (const k of ["cols", "rows"]) {
      if (Math.round(num(p.spec.grid[k])) !== Math.round(num(target[k]))) {
        return `part ${labelOf(p.index)} has ${k} ${p.spec.grid[k] ?? "?"} vs ${target[k] ?? "?"}`;
      }
    }
    for (const k of ["dx", "dy"]) {
      const a = num(p.spec.grid[k]);
      const b = num(target[k]);
      if (b <= 0 || Math.abs(a - b) / b > SPACING_TOL) return `part ${labelOf(p.index)} has ${k} ${a} vs ${b}`;
    }
  }
  return null;
};

const outOfBounds = (spec, grid) => {
  const cols = Math.round(num(grid.cols));
  const rows = Math.round(num(grid.rows));
  let bad = 0;
  for (const key of ["target_points", "given_points", "context_points", "original_shape"]) {
    for (const p of spec[key] || []) {
      if (!Array.isArray(p) || !Number.isFinite(num(p[0], NaN)) || !Number.isFinite(num(p[1], NaN))) continue;
      if (p[0] < 0 || p[0] > cols || p[1] < 0 || p[1] > rows) bad++;
    }
  }
  return bad;
};

export const UnifySharedGridsControl = ({ subQuestions, setSubQuestions, labelOf = (i) => String.fromCharCode(97 + i) }) => {
  const parts = gridParts(subQuestions);
  if (parts.length < 2) return null;

  const counts = {};
  parts.forEach((p) => { counts[p.key] = (counts[p.key] || 0) + 1; });
  const bestKey = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0];
  const target = parts.find((p) => p.key === bestKey) || parts[0];
  const unified = Object.keys(counts).length === 1 && parts.every((p) => JSON.stringify(p.spec.grid) === JSON.stringify(target.spec.grid));
  const reason = unified ? null : incompatibleReason(parts, target.spec.grid, labelOf);
  const stray = unified || reason ? [] : parts.filter((p) => outOfBounds(p.spec, target.spec.grid) > 0).map((p) => labelOf(p.index));

  const apply = () => {
    setSubQuestions((prev) => prev.map((sq, i) =>
      gridParts(prev).some((p) => p.index === i)
        ? { ...sq, interactiveSpec: { ...sq.interactiveSpec, image_url: target.spec.image_url, grid: { ...target.spec.grid } } }
        : sq
    ));
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", width: "100%" }}>
      <Button size="small" variant="outlined" disabled={unified || !!reason} onClick={apply} sx={{ textTransform: "none" }}>
        Unify grid across {parts.length} drawing parts
      </Button>
      {unified ? (
        <Typography variant="caption" color="text.secondary">All drawing parts already share one grid.</Typography>
      ) : reason ? (
        <Typography variant="caption" color="error">Different grid extents — {reason}. Fix the calibration first.</Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Copies part {labelOf(target.index)}'s image + calibration onto the others ({target.key}).
          {stray.length > 0 ? ` Warning: points on part${stray.length > 1 ? "s" : ""} ${stray.join(", ")} fall outside that grid.` : ""}
        </Typography>
      )}
    </Box>
  );
};

// The admin counterpart of the student's shared grid (StudentPaperTake): parts that all draw on ONE
// background image are authored on ONE canvas, switching which part the taps belong to, with every
// other part's authored answer painted in that part's colour. Same kind list and colours as the take
// page, so what the author sees here is what the student gets.
const SHARED_GRID_KINDS = ["points", "drag-shape", "polygon", "rect", "reflect", "rotate", "lines"];
const SHARED_GRID_COLORS = ["#1565c0", "#2e7d32", "#ef6c00", "#6a1b9a", "#c62828", "#00838f"];

const sharedGridParts = (subQuestions) => {
  const draws = (subQuestions || []).reduce((out, sq, index) => {
    const spec = sq?.interactiveSpec;
    if (spec?.image_url && SHARED_GRID_KINDS.includes(spec.kind)) out.push({ index, spec, key: gridKey(spec.image_url) });
    return out;
  }, []);
  if (draws.length < 2 || !draws[0].key) return [];
  return draws.every((p) => p.key === draws[0].key) ? draws : [];
};

// A part's authored answer in the shape InteractiveDiagramCanvas paints as a read-only context layer.
const authoredLayer = (spec, color, label) => {
  if (spec.kind === "lines") {
    const lines = (spec.target_lines || []).filter((l) => Array.isArray(l) && l.length === 2 && Array.isArray(l[0]));
    return lines.length ? { label, color, lines } : null;
  }
  const labels = Array.isArray(spec.point_labels) ? spec.point_labels : [];
  const points = (spec.target_points || [])
    .filter((p) => Array.isArray(p) && p.length >= 2)
    .map((p, i) => [p[0], p[1], typeof p[2] === "string" && p[2] ? p[2] : (labels[i] || "")]);
  const edges = (spec.required_edges || []).filter((e) => Array.isArray(e) && e.length === 2 && Array.isArray(e[0]));
  return points.length || edges.length ? { label, color, points, edges } : null;
};

export const SharedGridPartsEditor = ({ subQuestions, setSubQuestions, labelOf = (i) => String.fromCharCode(97 + i) }) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [open, setOpen] = useState(true);
  const parts = sharedGridParts(subQuestions);
  if (parts.length < 2) return null;

  const active = parts.find((p) => p.index === activeIndex) || parts[0];
  const colorOf = (index) => SHARED_GRID_COLORS[Math.max(0, parts.findIndex((p) => p.index === index)) % SHARED_GRID_COLORS.length];
  const layers = parts.filter((p) => p.index !== active.index)
    .map((p) => authoredLayer(p.spec, colorOf(p.index), labelOf(p.index)))
    .filter(Boolean);

  return (
    <Box sx={{ border: "1px solid #c7d2fe", borderRadius: 1, p: 1.5, mb: 2, bgcolor: "#f8faff", width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: open ? 1 : 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: colorOf(active.index) }}>
          Shared grid — editing part ({labelOf(active.index)}) of {parts.length} on one canvas
        </Typography>
        <Button size="small" onClick={() => setOpen((v) => !v)} sx={{ textTransform: "none" }}>
          {open ? "Hide" : "Show"}
        </Button>
      </Box>
      {open && (
        <>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            {parts.map((p) => (
              <Button
                key={p.index}
                size="small"
                variant={p.index === active.index ? "contained" : "outlined"}
                onClick={() => setActiveIndex(p.index)}
                sx={{
                  textTransform: "none", minWidth: 0, px: 1.25,
                  ...(p.index === active.index
                    ? { bgcolor: colorOf(p.index), "&:hover": { bgcolor: colorOf(p.index) } }
                    : { color: colorOf(p.index), borderColor: colorOf(p.index) }),
                }}
              >
                ({labelOf(p.index)}) {p.spec.kind}
              </Button>
            ))}
          </Box>
          <InteractiveDiagramEditor
            key={active.index}
            kind={active.spec.kind}
            spec={active.spec}
            imageUrl={active.spec.image_url}
            contextLayers={layers}
            onChange={(next) => setSubQuestions((prev) => prev.map((sq, i) => (i === active.index ? { ...sq, interactiveSpec: next } : sq)))}
          />
        </>
      )}
    </Box>
  );
};
